import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { PageHeader, KpiShell, KPI_ROW, PanelLoading } from '../components/ui.jsx'
import { BarChart } from '../components/charts.jsx'
import DataTable, { StatusPill } from '../components/DataTable.jsx'
import Select from '../components/Select.jsx'
import DayTimeline from '../components/DayTimeline.jsx'
import CallView from '../components/CallView.jsx'
import LeadTimelineView from '../components/LeadTimelineView.jsx'
import {
  CalendarIcon,
  ClockIcon,
  BuildingIcon,
  MailIcon,
  TextIcon,
  HashIcon,
  UserIcon,
  TagIcon,
  RefreshIcon,
  DownloadIcon,
  CheckSquareIcon,
  ChatIcon,
  CopyIcon,
  PhoneIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ActivityIcon,
  CheckIcon,
  SendIcon,
} from '../components/icons.jsx'
import { ROLE_DEFS } from '../lib/rbac.js'
import { roleOrEmpty } from '../lib/roles.js'
import { listEventsRange, eventsSummary, eventOutboxSize, absoluteTime } from '../lib/events.js'
import { listMembers } from '../lib/team.js'
import { hubspotOwners, hubspotMetrics, hubspotIndex, hubspotDailyReport, syncAge, formatAmount, humanizeEnum } from '../lib/hubspot.js'
import { getCachedCompanies } from '../lib/leadsCache.js'
import { annotateRows } from '../lib/data.jsx'
import { crmLeadRows, claimedCompanyIds } from '../lib/crmLeads.js'
import { CATEGORY_KEYS } from '../lib/categories.js'
import { buildLeadLinkIndex, leadLinkFor } from '../lib/leadLink.js'
import {
  rangeFor,
  streakWindow,
  RANGE_KEYS,
  LEGACY_RANGE_KEYS,
  RANGE_LABELS,
  DEFAULT_RANGE_KEY,
  isRangeKey,
  isDayKey,
  chicagoMidnightISO,
  buildDays,
  summarize,
  callOutcomeCoverage,
  normalizeDensity,
  densityBars,
  densityLabelPlan,
  densityFillingIndex,
  densityEmptyNote,
  DENSITY_VIEWS,
  DENSITY_VIEW_LABELS,
  densityViewOf,
  densityViewMismatchNote,
  densityAxis,
  densityZeroCaption,
  chicagoClock,
  chicagoDayOf,
  longDay,
  dayRangeLabel,
  dayStepPlan,
  isImpersonated,
  runOutcome,
  RUN_OUTCOME_LABELS,
  OPERATION_IDS,
  OPERATION_LABELS,
} from '../lib/activityMetrics.js'
import { dayTimeline, dayTimelineSummary, timelineDayOf, TIMELINE_EVENT_TYPES, GAP_MIN_MINUTES } from '../lib/dayTimeline.js'
import { callOutcome, callPickup, callSourceLabel, formatDurationMs } from '../lib/callContent.js'
import { toCsv, downloadCsv, fileSlug } from '../lib/csvExport.js'

// ACTIVITY — one member's day-by-day record, built for a reader who wants to
// check the claim. Every tile is a button; pressing it opens the rows the
// number was computed from, with the rule in plain English above the table.
//
// Two sources, never blended silently:
//   - the workspace events ledger (GET /api/events): what the app recorded the
//     member doing — operation runs, questions, copies, HubSpot clicks,
//     sign-ins. The server stamps the actor from the verified sign-in token,
//     so a row says WHO; the app client reports WHAT.
//   - the HubSpot mirror (GET /api/hubspot/metrics): what actually reached
//     prospects — outreach, follow-ups, replies, responses, deals, tasks,
//     calls, meetings and notes.
//
// What this page refuses to do: show a zero it did not compute. A failed
// read, an incomplete scan, or the browser-local mirror (mode:'local') is
// rendered as a dash with the reason, because an investor reading "0" would
// believe it.

// Types the tiles need before anything can be said: the routine needs every
// operation_run row (the streak predicate reads data.recorded etc., which the
// per-day summary cannot express), and the drafts section needs unique
// company keys from draft_copied / hubspot_email_opened rows. Questions and
// sign-ins are counted from the summary and their rows load when opened.
const EAGER_TYPES = ['operation_run', 'draft_copied', 'hubspot_email_opened']

// A SECOND, separately cached read of operation_run rows — same event type,
// different window. It exists only for ranges that do not end today (see
// runsWindow below): merging the streak's window into such a range's read
// would stretch one request from the range's first day all the way to today,
// which for an old custom range is thousands of days and a walk that stops
// early — dropping the OLDEST rows, i.e. exactly the days the reader picked.
// listEventsRange is keyed by the args it is given, and rowsByType/cache are
// keyed by this logical name, so the two reads never overwrite each other.
const RUNS_STREAK_TYPE = 'operation_run@streak'
// The real ledger type a logical type reads. Everything except the streak read
// is its own event type.
const eventTypeOf = (type) => (type === RUNS_STREAK_TYPE ? 'operation_run' : type)

// Plain names for the eager reads, for the incomplete-read banner.
const EAGER_LABELS = {
  operation_run: 'operation-run',
  draft_copied: 'draft-copied',
  hubspot_email_opened: 'HubSpot-opened',
  [RUNS_STREAK_TYPE]: 'streak operation-run',
  question_asked: 'questions-asked',
  sign_in: 'sign-in',
}
// The ledger types the day timeline draws that the tiles do not already
// read eagerly. Read with the eager set so the column is whole on first
// paint rather than filling in when a detail happens to be opened.
const TIMELINE_EXTRA_TYPES = TIMELINE_EVENT_TYPES.filter((t) => !EAGER_TYPES.includes(t))

const IDENTITY_KEY = (member) => `revailosales.activityIdentity.${String(member || '').toLowerCase()}`

// HOW FAR BACK THE STREAK LOOKS, whatever the range on screen says.
//
// The page opens on today, and a streak counted over a one-day window can only
// say 0 or 1 — "0 days" every morning, which reads as "the routine broke". So
// the operation-run rows are read over this many days ending today (a wider
// window than the tiles chart), the streak is counted over those, and the tile
// note says which window it used. 120 days is long enough that a real streak is
// never clipped in practice and short enough that the read stays quick; when
// the selected range ENDS TODAY and reaches further back than this, the range
// wins and one read serves both (runsWindow). A range that does not end today
// cannot be merged — see the comment there — and is read separately.
// See summarize({ streakDays }) in lib/activityMetrics.js.
const STREAK_WINDOW_DAYS = 120

// CHART CARDS CAP THEMSELVES AT 900px, and only they do. The page fills the
// pane (the summary container carries no max-width) because tiles and tables
// genuinely use the room — a 15-column ledger wants every pixel. A bar chart
// does not: stretched across a wide monitor its bars grow to 90px slabs and
// the shape of the day stops being readable, which is the whole point of the
// chart. So the cap lives HERE, on the card, not on the page: charts stay a
// readable measure while everything around them uses the full width.
// Every current use of CARD is a chart (operations, questions) — if a
// non-chart section ever wants this class string, give it its own without the
// cap rather than widening this one.
// THE CHART CARDS ARE CAPPED, THE PAGE IS NOT. The summary's own width cap was
// removed so the tiles fill the pane, but a bar chart does not improve by being
// stretched: the same handful of bars just drift apart until the shape stops
// reading as a shape. 900px keeps a day's 24 hourly bars adjacent enough to
// scan as a profile. Used by the two capped chart sections and nothing else —
// the tile rows and the detail tables are deliberately full width.
const CARD = 'mb-5 max-w-[900px] rounded-[12px] border border-line bg-surface p-5 shadow-card'
// THE ONE DELIBERATE EXCEPTION, at the user's request: the activity-density
// card, and only it, spans the full width of the page. Everything the comment
// above says about a capped chart is still true of a chart whose bar count is
// KNOWN — the other two draw one bar per day in the range and no more. This one
// does not: its bar count runs from 24 (an hour-of-day profile, or a day of
// hours) to several hundred (a quarter of daily bars, clipped at the server's
// 400-point ceiling), and at the top of that range 900px puts the bars at less
// than two pixels each. A chart whose whole job is the SHAPE of the work needs
// the pixels the page can give it, so this card takes them and the other two
// keep the measure that suits a handful of bars.
const WIDE_CARD = 'mb-5 rounded-[12px] border border-line bg-surface p-5 shadow-card'
// The content frame the app shell used to draw around this page (AppLayout:
// max-w 1400, centred, 24px / 16px on phones). The page is SELF_FRAMED there so
// its two scroll surfaces — the summary and the detail page — span the whole
// pane and the scrollbar sits on the pane's edge; this puts the same measure
// back on their children, so the content does not move.
const PAGE_FRAME = 'mx-auto w-full max-w-[1400px] px-6 max-md:px-4'
// The four section headings. Bigger than the 13px dim label they used to be
// and painted in `text-ink` so they read as headings rather than as captions
// on the row above. 16px, not 15: at 15 they sat one pixel and one letter-case
// away from the card titles under them (`text-[14px] font-semibold text-ink`),
// which is not a level, it is a coincidence — a reader has to be able to see
// that a card belongs UNDER a section. Uppercase and the letter-spacing carry
// the rest of that difference. Both themes get the same contrast because
// `--ink` is the theme's own foreground.
// Title case, not all-caps. The wide tracking went with the `uppercase`:
// letter-spacing exists to keep CAPITALS legible, and on mixed-case text it
// just pulls the words apart.
const SECTION_H = 'm-0 mb-2 text-[16px] font-semibold text-ink'
const BOX = 'rounded-[12px] border border-line bg-surface p-10 text-center text-dim'
// A loading line is not a card: no border, no fill, just the words centred in
// the space the record is about to take - user request, 2026-10-11. In BOX it
// read as a wide empty panel stretched across the page.
const LOADING = 'flex flex-1 items-center justify-center p-10 text-center text-dim'
// The page's warning band, said once. Every other copy of this string in the
// file predates the constant; new banners use this one.
const WARN_BOX =
  'mb-5 rounded-[12px] border border-[#fde68a] bg-[#fefce8] p-4 text-[13px] text-[#a16207] dark:border-[rgba(161,98,7,0.4)] dark:bg-[rgba(161,98,7,0.14)] dark:text-[#fbbf24]'
// Its green counterpart, for the one thing on this page that is an action
// with an outcome worth confirming (the daily report went out, to whom).
const OK_BOX =
  'mb-5 rounded-[12px] border border-[#bbf7d0] bg-[#f0fdf4] p-4 text-[13px] text-[#166534] dark:border-[rgba(22,163,74,0.4)] dark:bg-[rgba(22,163,74,0.14)] dark:text-[#4ade80]'
const GHOST_BTN =
  'inline-flex cursor-pointer items-center gap-x-[7px] rounded-[9px] border border-line bg-surface px-3 py-2 text-[13px] font-semibold text-ink transition-colors hover:bg-panel disabled:cursor-not-allowed disabled:opacity-45'
const INPUT =
  'w-[240px] max-w-full rounded-[9px] border border-line bg-surface px-[12px] py-[8px] text-[13px] text-ink placeholder:text-faint focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)] focus:outline-none'
const SELECT_BTN = 'min-w-[160px]'
// The calendar page's range chips — the inline filter-button family
// (FilterChip's chipClass), so a lit chip reads as "this window is on".
const RANGE_CHIP_ON =
  'inline-flex cursor-pointer items-center rounded-[10px] border border-primary bg-primary-tint px-3 py-2 text-[13px] font-semibold text-primary-dark'
const RANGE_CHIP_OFF =
  'inline-flex cursor-pointer items-center rounded-[10px] border border-line bg-surface px-3 py-2 text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink'
// The day stepper's two arrow buttons.
const STEP_BTN =
  'inline-flex h-[34px] w-[34px] cursor-pointer items-center justify-center rounded-[9px] border border-line bg-surface text-dim hover:bg-panel hover:text-ink disabled:cursor-not-allowed disabled:opacity-40'

const num = (v) => (v == null ? '—' : Number(v).toLocaleString())
// The word that goes with a count: singular for exactly 1, plural otherwise
// (0 calls, 1 call, 2 calls).
const plural = (v, one, many) => (Number(v) === 1 ? one : many)
const pct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`)

function firstName(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)[0]
    .toLowerCase()
}

function readIdentity(member) {
  try {
    const raw = localStorage.getItem(IDENTITY_KEY(member))
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

function writeIdentity(member, value) {
  try {
    localStorage.setItem(IDENTITY_KEY(member), JSON.stringify(value))
  } catch {
    /* storage full or blocked — the URL still carries the choice */
  }
}

// Plain-English reason for a ledger read that did not come back from the API.
function ledgerFailure(res) {
  const status = res && res.status
  if (status === 401) return 'Your sign-in has expired — sign in again to read the ledger.'
  if (status === 403) return 'Your account is not a member of this workspace, so the ledger cannot be read.'
  if (status) return `The events ledger answered ${status} — numbers are unavailable, not zero.`
  return 'The events ledger could not be reached from this browser — numbers are unavailable, not zero.'
}

function metricsFailure(m) {
  if (!m) return 'HubSpot results have not been read.'
  if (m.status === 401) return 'Your sign-in has expired — sign in again to read HubSpot results.'
  if (m.forbidden) return m.reason || 'Your account is not a member of this workspace.'
  if (m.available === false) return m.reason || 'The HubSpot mirror could not be reached.'
  if (m.configured === false) return m.reason || 'HubSpot is not connected to this workspace.'
  if (m.complete === false) return m.reason || 'The HubSpot scan did not finish — results withheld rather than shown short.'
  return ''
}

// Sum of a {label: n} counts object, or the number itself. operation_run.data.tasks
// is a counts map on the worklist operation and absent elsewhere.
function countOf(value) {
  if (value == null) return null
  if (typeof value === 'number') return value
  if (typeof value === 'object') {
    let t = 0
    for (const v of Object.values(value)) if (typeof v === 'number') t += v
    return t
  }
  return null
}

function HubSpotLink({ url, label = 'Open' }) {
  if (!url) return <span className="text-faint">—</span>
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="whitespace-nowrap font-semibold text-primary-dark hover:underline"
      // A link inside a pressable row (the calls ledger) opens HubSpot and
      // must not ALSO open the row it sits in.
      onClick={(e) => e.stopPropagation()}
    >
      {label} ↗
    </a>
  )
}

// THE LEAD PAGE, FROM A MIRROR ROW. Every row in these tables names its
// company by HubSpot's id; the lead page is addressed by the directory's key.
// The inversion lives in lib/leadLink.js and the built index rides down here on
// a context rather than through DataTable, because a column's `render` is
// handed nothing but the row.
//
// A cell that cannot link says WHY on hover instead of drawing a dead link:
// "no lead matches this company" and "the directory has not been read yet" are
// different facts, and a bare dash would tell the reader the first one while
// the second is still true.
const LeadLinkContext = createContext({ index: null, loading: false })

function LeadCell({ companyId }) {
  const { index, loading } = useContext(LeadLinkContext)
  const link = leadLinkFor(index, companyId, { loading })
  if (link.status === 'ok') {
    return (
      <Link
        to={link.href}
        // The row itself, handed over exactly as the leads table and the global
        // search hand it over: the lead page renders from it immediately, and
        // for a CRM-only company (keyed 'hs-<id>', built at render time and
        // never cached) it is the only thing that makes the page resolve at all.
        state={link.state}
        className="whitespace-nowrap font-semibold text-primary-dark hover:underline"
        title="Open this company’s lead page"
        // Inside a pressable row (the thread tables): open the lead page, not
        // the row's thread as well.
        onClick={(e) => e.stopPropagation()}
      >
        Lead →
      </Link>
    )
  }
  return (
    <span className="text-faint" title={link.reason}>
      {link.status === 'loading' ? '…' : '—'}
    </span>
  )
}

// THE THREAD BEHIND A ROW. LeadTimelineView reads a company's history by its
// HubSpot id AND by the addresses on the wire (mail Bo sent straight from Gmail
// is only found by address), so a row hands over both: the company it names,
// and the address its email went to or came from. A deal, task, note or
// meeting row has no address; its company alone opens the thread.
const WIRE_ADDRESS = /[^\s<>"',;:]+@[^\s<>"',;:]+\.[a-z]{2,}/i
function threadLeadOf(row) {
  const wire = [row.to, row.from, row.company_key].map((v) => String(v || '')).join(' ')
  const email = ((wire.match(WIRE_ADDRESS) || [''])[0] || '').toLowerCase()
  return {
    key: String(row.company_key || row.company_id || row.id || ''),
    name: row.company_name || email || 'This company',
    email,
    contacts: wire,
  }
}

const leadCol = () =>
  col('lead', 'Lead', (r) => r.company_id || '', {
    sortable: false,
    render: (r) => <LeadCell companyId={r.company_id} />,
  })

function When({ iso }) {
  if (!iso) return <span className="text-faint">—</span>
  return (
    <span className="whitespace-nowrap font-mono text-[12.5px] tabular-nums text-dim" title={iso}>
      {absoluteTime(iso)}
    </span>
  )
}

function Mono({ children }) {
  return <span className="font-mono text-[12.5px] tabular-nums">{children}</span>
}

const RUN_TONE = { built: 'success', nothing: 'info', unreadable: 'warning', failed: 'danger', aborted: 'neutral', unknown: 'neutral' }

// ── columns ─────────────────────────────────────────────────────────────────
// Each column carries `value(row)` — the primitive the CSV export writes and
// the default sort key — next to `render`, so the file an investor downloads
// holds exactly what the table showed.
function col(key, header, value, extra = {}) {
  return {
    key,
    header,
    sortable: true,
    value,
    sortValue: value,
    render: (r) => {
      const v = value(r)
      return v == null || v === '' ? <span className="text-faint">—</span> : String(v)
    },
    ...extra,
  }
}

const whenCol = (key, header, pick) =>
  col(key, header, pick, { icon: ClockIcon, render: (r) => <When iso={pick(r)} /> })

const numCol = (key, header, pick, extra = {}) =>
  col(key, header, pick, {
    icon: HashIcon,
    numeric: true,
    render: (r) => {
      const v = pick(r)
      return v == null ? <span className="text-faint">—</span> : <Mono>{Number(v).toLocaleString()}</Mono>
    },
    ...extra,
  })

const linkCol = (key = 'hubspot', header = 'HubSpot') =>
  col(key, header, (r) => r.hubspot_url || '', { sortable: false, render: (r) => <HubSpotLink url={r.hubspot_url} /> })

const DAY_COLUMNS = [
  col('day', 'Day', (r) => r.day, {
    icon: CalendarIcon,
    render: (r) => (
      <span className={`whitespace-nowrap ${r.businessDay ? 'font-semibold text-ink' : 'text-dim'}`}>
        {longDay(r.day)}
        {!r.businessDay && <span className="ml-1 text-[11px] text-faint">weekend</span>}
      </span>
    ),
  }),
  ...OPERATION_IDS.map((id) =>
    col(`op_${id}`, OPERATION_LABELS[id], (r) => (r.ops[id] ? chicagoClock(r.ops[id]) : ''), {
      icon: CheckSquareIcon,
      sortValue: (r) => r.ops[id] || '',
      render: (r) =>
        r.ops[id] ? (
          <span className="whitespace-nowrap text-good" title={r.ops[id]}>
            ✓ {chicagoClock(r.ops[id])}
          </span>
        ) : (
          <span className="text-faint">—</span>
        ),
    }),
  ),
  col('allThree', 'All 3', (r) => (r.allThree ? 'yes' : 'no'), {
    sortValue: (r) => (r.allThree ? 1 : 0),
    render: (r) =>
      r.allThree ? (
        <StatusPill tone="success">
          All 3
        </StatusPill>
      ) : (
        <span className="text-faint">{r.opsCompleted}/3</span>
      ),
  }),
  numCol('questions', 'Questions', (r) => r.questions),
  numCol('copies', 'Copies', (r) => r.copies),
  numCol('hubspotOpens', 'HubSpot opens', (r) => r.hubspotOpens),
  numCol('outreach', 'Outreach', (r) => r.outreach),
  numCol('followups', 'Follow-ups', (r) => r.followups),
  numCol('replies', 'Replies', (r) => r.replies),
  numCol('responses', 'Responses', (r) => r.responses),
  numCol('deals', 'Deals', (r) => r.deals),
  numCol('tasks', 'Tasks', (r) => r.tasks),
  // The mirror's engagement columns. Like every other HubSpot column here they
  // are blank — never 0 — on a day the mirror was not read for; numCol prints
  // the dash and the CSV an empty cell.
  numCol('calls', 'Calls', (r) => r.calls),
  numCol('meetings', 'Meetings', (r) => r.meetings),
  numCol('notes', 'Notes', (r) => r.notes),
  numCol('signIns', 'Sign-ins', (r) => r.signIns),
]

// The streak ledger's columns: the day, the three operations and All 3, and
// nothing else. The streak's rows are built from operation-run rows ALONE —
// no summary, no copies, no mirror over that wider window — so every other
// DAY_COLUMNS cell would print a 0 that was never computed, which is the one
// thing this page refuses to do. Derived from DAY_COLUMNS by key so a column
// added there cannot silently reappear here.
const STREAK_DAY_COLUMNS = DAY_COLUMNS.filter((c) => c.key === 'day' || c.key.startsWith('op_') || c.key === 'allThree')

const QUESTION_COLUMNS = [
  whenCol('at', 'At', (r) => r.createdAt),
  col('conversation', 'Conversation', (r) => (r.data && r.data.conversationId) || '', {
    icon: ChatIcon,
    render: (r) => {
      const id = r.data && r.data.conversationId
      if (!id) return <span className="text-faint">—</span>
      return (
        <Link to={`/chat/${encodeURIComponent(id)}`} className="font-mono text-[12px] text-primary-dark hover:underline">
          {String(id).slice(0, 12)}
        </Link>
      )
    },
  }),
  col('preview', 'Question', (r) => (r.data && r.data.preview) || r.detail || '', { icon: TextIcon, wrap: true, maxWidth: 520 }),
  numCol('chars', 'Chars', (r) => (r.data && r.data.chars) ?? null),
]

const COPY_COLUMNS = [
  whenCol('at', 'At', (r) => r.createdAt),
  col('company', 'Company', (r) => (r.data && r.data.company) || (r.data && r.data.key) || '', { icon: BuildingIcon, render: (r) => <span className="font-semibold">{(r.data && (r.data.company || r.data.key)) || '—'}</span> }),
  col('kind', 'Kind', (r) => (r.data && r.data.kind) || '', {
    icon: TagIcon,
    render: (r) => (
      <StatusPill tone={r.data && r.data.kind === 'subject' ? 'purple' : 'info'} icon={CopyIcon}>
        {r.data && r.data.kind === 'subject' ? 'Subject' : 'Email'}
      </StatusPill>
    ),
  }),
  col('contact', 'Contact', (r) => (r.data && r.data.email) || '', { icon: MailIcon }),
  numCol('touch', 'Touch', (r) => (r.data && r.data.touch) ?? null),
  col('operation', 'Operation', (r) => (r.data && r.data.operationId) || '', { icon: CheckSquareIcon, render: (r) => OPERATION_LABELS[r.data && r.data.operationId] || (r.data && r.data.operationId) || <span className="text-faint">—</span> }),
  col('hubspot', 'HubSpot', (r) => (r.data && r.data.hubspotUrl) || '', { sortable: false, render: (r) => <HubSpotLink url={r.data && r.data.hubspotUrl} /> }),
]

const OPEN_KIND = { email: 'Email in HubSpot', record: 'Open in HubSpot', search: 'Find in HubSpot' }
const OPEN_COLUMNS = [
  whenCol('at', 'At', (r) => r.createdAt),
  col('company', 'Company', (r) => (r.data && r.data.company) || (r.data && r.data.key) || '', { icon: BuildingIcon, render: (r) => <span className="font-semibold">{(r.data && (r.data.company || r.data.key)) || '—'}</span> }),
  col('kind', 'Kind', (r) => OPEN_KIND[r.data && r.data.kind] || (r.data && r.data.kind) || '', { icon: TagIcon }),
  col('url', 'Link', (r) => (r.data && r.data.url) || '', { sortable: false, render: (r) => <HubSpotLink url={r.data && r.data.url} /> }),
]

const companyCol = () =>
  col('company', 'Company', (r) => r.company_name || r.company_key || '', {
    icon: BuildingIcon,
    render: (r) => (
      <div className="min-w-0">
        <div className="truncate font-semibold">{r.company_name || <span className="text-faint">unknown company</span>}</div>
        {!r.company_name && r.company_key && <div className="truncate font-mono text-[11px] text-faint">{r.company_key}</div>}
      </div>
    ),
  })

const attributedCol = () =>
  col('attributed_by', 'Attributed by', (r) => r.attributed_by || '', {
    icon: UserIcon,
    render: (r) => (
      <StatusPill tone={r.attributed_by === 'sender' || r.attributed_by === 'creator' ? 'success' : 'neutral'}>
        {r.attributed_by || 'unknown'}
      </StatusPill>
    ),
  })

const toCol = () => col('to', 'To', (r) => (Array.isArray(r.to) ? r.to.join(', ') : r.to || ''), { icon: MailIcon, wrap: true, maxWidth: 260 })
// ONE LINE, TRUNCATED, WITH THE FULL TEXT IN THE TOOLTIP — not a wrapping cell.
// A wrapping column has no width floor under the browser's auto table layout,
// so next to six nowrap columns the subject collapsed into a one-word-per-line
// sliver: the column a reader actually scans, made unreadable. Truncation is
// legible at a glance, the title attribute carries the whole subject, and the
// CSV export carries it untouched. (`table-layout: fixed` would let a width
// stick, but DataTable is shared by every page in the app.)
const subjectCol = () => col('subject', 'Subject', (r) => r.subject || '', { icon: TextIcon, maxWidth: 340 })

const OUTREACH_COLUMNS = [
  whenCol('at', 'First touch', (r) => r.at),
  companyCol(),
  toCol(),
  subjectCol(),
  col('status', 'Status', (r) => r.status || 'delivered', { render: (r) => humanizeEnum(r.status || 'delivered') }),
  attributedCol(),
  linkCol(),
]

const FOLLOWUP_COLUMNS = [
  whenCol('at', 'At', (r) => r.at),
  companyCol(),
  numCol('touch', 'Touch #', (r) => r.touch ?? null),
  toCol(),
  subjectCol(),
  attributedCol(),
  linkCol(),
]

const REPLY_COLUMNS = [whenCol('at', 'At', (r) => r.at), companyCol(), toCol(), subjectCol(), attributedCol(), linkCol()]

const RESPONSE_COLUMNS = [
  whenCol('at', 'At', (r) => r.at),
  companyCol(),
  col('from', 'From', (r) => r.from || '', { icon: MailIcon }),
  subjectCol(),
  col('snippet', 'Snippet', (r) => r.snippet || '', { icon: TextIcon, maxWidth: 320, sortable: false }),
  whenCol('answers_at', 'Answers our email of', (r) => r.answers_at),
  // Off by default, on in the Columns picker. Eight columns squeeze the subject
  // — the thing a reader is actually scanning for — into a vertical sliver, and
  // "Logged as" only ever says something surprising for the handful of replies
  // HubSpot recorded as outbound; it earns a look, not a permanent column.
  col('logged_as', 'Logged as', (r) => r.logged_as || 'INCOMING_EMAIL', {
    defaultHidden: true,
    icon: TagIcon,
    render: (r) => (
      <StatusPill tone={r.logged_as && r.logged_as !== 'INCOMING_EMAIL' ? 'warning' : 'neutral'}>{humanizeEnum(r.logged_as || 'INCOMING_EMAIL')}</StatusPill>
    ),
  }),
  // The lead page beside the HubSpot record: one link out to the CRM, one in
  // to this app's own page for the same company.
  leadCol(),
  linkCol(),
]

const DEAL_COLUMNS = [
  whenCol('created_at', 'Created', (r) => r.created_at),
  col('name', 'Deal', (r) => r.name || '', { icon: TextIcon, render: (r) => <span className="font-semibold">{r.name || '—'}</span> }),
  // STAGE, ONLY WHEN IT MEANS SOMETHING TO A READER.
  //
  // HubSpot's `dealstage` is an internal name on the default pipeline
  // ("appointmentscheduled") but a bare numeric id on a custom one
  // ("1312174082") — and this portal uses a custom pipeline, so the column was
  // showing investors an opaque number. Pipeline stage LABELS are not mirrored
  // (hubspot-sync mirrors objects, not pipeline definitions), so there is
  // nothing to translate the id with. What the mirror does carry honestly is
  // whether the deal is open, won or lost, which is the thing anyone reading a
  // deal list actually wants; the raw stage travels in the tooltip and the CSV
  // so nothing is lost for someone reconciling against HubSpot.
  col('stage', 'Status', (r) => (r.is_won ? 'won' : r.is_closed ? 'lost' : 'open'), {
    render: (r) => {
      const raw = String(r.stage || '')
      const numeric = /^\d+$/.test(raw)
      return (
        <StatusPill tone={r.is_won ? 'success' : r.is_closed ? 'neutral' : 'info'}>
          <span title={raw ? `HubSpot stage ${numeric ? `id ${raw}` : humanizeEnum(raw)}` : 'No stage on the record'}>
            {r.is_won ? 'Won' : r.is_closed ? 'Closed lost' : 'Open'}
            {raw && !numeric ? ` · ${humanizeEnum(raw)}` : ''}
          </span>
        </StatusPill>
      )
    },
  }),
  numCol('amount', 'Amount', (r) => (r.amount == null || r.amount === '' ? null : Number(r.amount)), { render: (r) => <Mono>{r.amount == null || r.amount === '' ? '—' : formatAmount(r.amount)}</Mono> }),
  companyCol(),
  col('owner_email', 'Owner', (r) => r.owner_email || '', { icon: UserIcon }),
  attributedCol(),
  linkCol(),
]

const TASK_COLUMNS = [
  whenCol('created_at', 'Created', (r) => r.created_at),
  col('subject', 'Task', (r) => r.subject || '', { icon: TextIcon, wrap: true, maxWidth: 360, render: (r) => <span className="font-semibold">{r.subject || '—'}</span> }),
  col('status', 'Status', (r) => r.status || '', { render: (r) => <StatusPill tone={String(r.status || '').toUpperCase() === 'COMPLETED' ? 'success' : 'warning'}>{humanizeEnum(r.status || 'unknown')}</StatusPill> }),
  whenCol('due_at', 'Due', (r) => r.due_at),
  companyCol(),
  col('owner_email', 'Owner', (r) => r.owner_email || '', { icon: UserIcon }),
  attributedCol(),
  linkCol(),
]

// ── calls, meetings and notes ───────────────────────────────────────────────
//
// The one rule these three tables exist to keep: a field HubSpot never recorded
// is a DASH, never a zero and never a guess. This portal returns
// hs_call_disposition and hs_call_duration empty on every call it holds, so the
// sync omits those keys entirely — and a "0m" column would then invent hundreds
// of zero-length calls while a "no answer" column would invent hundreds of
// failed ones, both of them about a person named at the top of the page.

// A call's length in the way a rep reads a call log, or null when HubSpot never
// recorded one. Absent (or unusable) is null and gets the dash; a duration that
// really is 0 is a real zero-length call and prints as 0s, which is a different
// claim from "we were not told".
function callDuration(ms) {
  return formatDurationMs(ms)
}

// The dash every unrecorded HubSpot field gets, with the reason on it. The
// tooltip is the whole point: an unexplained dash in an Outcome column reads as
// a broken page, and the reader's next guess is worse than the truth.
function NotRecorded({ what }) {
  return (
    <span className="text-faint" title={`HubSpot holds no ${what} — not recorded, which is not the same as zero.`}>
      —
    </span>
  )
}

// The engagement's own text, stripped of markup and cut to 160 characters by
// the server. One line with the full snippet in the cell's title (DataTable
// supplies it from the row value), for the reason spelled out on subjectCol.
const bodyCol = (header = 'Body', width = 380) =>
  col('body', header, (r) => r.body || '', { icon: TextIcon, maxWidth: width })

const CALL_COLUMNS = [
  whenCol('at', 'At', (r) => r.at),
  companyCol(),
  col('title', 'Title', (r) => r.title || '', { icon: TextIcon, maxWidth: 260 }),
  // THE OUTCOME IS A WORD NOW. HubSpot stores a call's disposition as an
  // opaque id on this portal; the mirror translates it through the portal's
  // own outcome list and sends `outcome`. An id it could not translate is
  // still shown AS an id (callOutcome), never dressed up as a word, and a
  // call with no disposition at all gets the dash with its reason.
  col('outcome', 'Outcome', (r) => (callOutcome(r) || {}).label || '', {
    render: (r) => {
      const o = callOutcome(r)
      if (!o) return <NotRecorded what="outcome for this call" />
      return (
        <StatusPill tone={o.opaque ? 'neutral' : 'info'}>
          <span title={o.opaque ? `HubSpot disposition id ${o.raw} — not in the portal's outcome list` : undefined}>{o.label}</span>
        </StatusPill>
      )
    },
  }),
  // WHETHER ANYONE PICKED UP — the mirror's verdict from the outcome, else
  // the dial status, else an inbound call's answer disposition. Never from
  // the duration, which counts the ringing and a voicemail too. No verdict
  // is a dash with its reason, not a "no".
  col('pickup', 'Picked up', (r) => (callPickup(r) || {}).label || '', {
    icon: PhoneIcon,
    render: (r) => {
      const p = callPickup(r)
      if (!p) return <NotRecorded what="answer record for this call — no outcome, dial status or answer disposition" />
      return <StatusPill tone={p.tone}>{p.label}</StatusPill>
    },
  }),
  col('duration', 'Duration', (r) => callDuration(r.duration_ms) || '', {
    icon: ClockIcon,
    align: 'right',
    // Sorted by the number, not by the string ("9s" must not sort after "10m").
    // A call with no recorded duration sorts below every call that has one
    // rather than pretending to be the shortest — hence the EMPTY STRING and
    // not -1. DataTable parks empties last in both directions (it checks for
    // '' before it compares), while -1 is just a number: ascending, it would
    // file every unrecorded call above a call HubSpot genuinely recorded as
    // zero seconds, which is the claim this dash exists to refuse.
    sortValue: (r) => (callDuration(r.duration_ms) == null ? '' : Number(r.duration_ms)),
    render: (r) => {
      const text = callDuration(r.duration_ms)
      return text == null ? <NotRecorded what="duration for this call" /> : <Mono>{text}</Mono>
    },
  }),
  // HubSpot phone or logged by hand — which decides whether HubSpot holds a
  // recording, a transcript and an AI summary for the call at all.
  col('source', 'Via', (r) => callSourceLabel(r), { icon: HashIcon, maxWidth: 140 }),
  // No Content column (removed 2026-10-11, user request): which records the
  // mirror holds for a call — notes, summary, recording, transcript — is what
  // pressing the row shows (CallView), so the table does not repeat it.
  bodyCol('Notes', 300),
  attributedCol(),
  linkCol(),
]

const NOTE_COLUMNS = [whenCol('at', 'At', (r) => r.at), companyCol(), bodyCol('Note', 560), attributedCol(), linkCol()]

const MEETING_COLUMNS = [
  // A MEETING IS DATED BY WHEN IT WAS HELD when HubSpot knows that, and by the
  // stamp HubSpot dates the record by when it does not — two different facts,
  // so the cell says which one the reader is looking at instead of quietly
  // presenting a fallback stamp as a start time. The marker says no more than
  // that: the server's fallback chain is hs_timestamp then hs_createdate and
  // the row does not carry which one it used, so "when it was logged" would be
  // a provenance the payload never stated. The fallback is not cosmetic — it
  // is the instant the server bucketed the row onto its day, so this column
  // always agrees with the Days ledger.
  //
  // `value` is spelled out separately because the CSV is the copy that gets
  // forwarded, and it carries neither the chip nor its tooltip: without the
  // marker in the cell itself the download would be a row asserting a start
  // time HubSpot never recorded, which is the lie this column exists to avoid.
  col('starts_at', 'Starts', (r) => r.starts_at || r.at || '', {
    icon: ClockIcon,
    value: (r) => (r.starts_at ? r.starts_at : r.at ? `${r.at} (no start time recorded — the stamp HubSpot dates the record by)` : ''),
    sortValue: (r) => r.starts_at || r.at || '',
    render: (r) =>
      r.starts_at ? (
        <When iso={r.starts_at} />
      ) : r.at ? (
        <span
          className="whitespace-nowrap"
          title="HubSpot recorded no start time for this meeting, so this is the stamp HubSpot dates the record by — the same instant it is counted on in the Days ledger."
        >
          <When iso={r.at} /> <span className="text-[11px] text-faint">no start time</span>
        </span>
      ) : (
        <NotRecorded what="start time for this meeting" />
      ),
  }),
  companyCol(),
  col('title', 'Title', (r) => r.title || '', { icon: TextIcon, maxWidth: 280 }),
  whenCol('ends_at', 'Ends', (r) => r.ends_at),
  col('outcome', 'Outcome', (r) => r.outcome || '', {
    render: (r) =>
      r.outcome ? (
        <StatusPill tone={String(r.outcome).toUpperCase() === 'COMPLETED' ? 'success' : 'neutral'}>{humanizeEnum(r.outcome)}</StatusPill>
      ) : (
        <NotRecorded what="outcome for this meeting" />
      ),
  }),
  bodyCol('Body', 320),
  attributedCol(),
  linkCol(),
]

// EVERY SEND, BOUNCES INCLUDED (2026-10-20) — the rows behind Emails Sent,
// Delivery Rate, Open Rate and Click Rate. Opens/Clicks are HubSpot's tracker
// counts; an untracked send shows a dash there rather than a 0 it never
// measured, and a bounce carries the receiving server's reason in its tooltip.
const SEND_COLUMNS = [
  whenCol('at', 'Sent', (r) => r.at),
  companyCol(),
  toCol(),
  subjectCol(),
  col('status', 'Delivery', (r) => (r.delivered ? 'Delivered' : humanizeEnum(r.status || 'undelivered')), {
    icon: CheckIcon,
    render: (r) => (
      <span title={r.bounce || undefined}>
        <StatusPill tone={r.delivered ? 'success' : 'danger'}>{r.delivered ? 'Delivered' : humanizeEnum(r.status || 'undelivered')}</StatusPill>
      </span>
    ),
  }),
  numCol('opens', 'Opens', (r) => (r.tracked ? r.opens ?? 0 : null)),
  numCol('clicks', 'Clicks', (r) => (r.tracked ? r.clicks ?? 0 : null)),
  col('source', 'Sent via', (r) => (r.source === 'apollo' ? 'Apollo' : 'HubSpot'), { icon: TagIcon }),
  col('bounce', 'Bounce reason', (r) => r.bounce || '', { defaultHidden: true, icon: TextIcon, maxWidth: 320 }),
  linkCol(),
]

const RUN_COLUMNS = [
  whenCol('at', 'At', (r) => r.createdAt),
  col('operation', 'Operation', (r) => OPERATION_LABELS[r.data && r.data.operationId] || (r.data && r.data.operationId) || '', { icon: CheckSquareIcon }),
  col('outcome', 'Outcome', (r) => RUN_OUTCOME_LABELS[runOutcome(r)], {
    render: (r) => {
      const o = runOutcome(r)
      return (
        <span className="inline-flex items-center gap-x-2">
          <StatusPill tone={RUN_TONE[o]}>
            {RUN_OUTCOME_LABELS[o]}
          </StatusPill>
          {o === 'failed' && r.data && r.data.error ? <span className="truncate text-[12px] text-faint" title={r.data.error}>{String(r.data.error).slice(0, 60)}</span> : null}
        </span>
      )
    },
  }),
  numCol('selected', 'Selected', (r) => (r.data && r.data.selected) ?? null),
  col('batch', 'New / Carried', (r) => (r.data && r.data.batch ? `${r.data.batch.fresh ?? '?'} / ${r.data.batch.carried ?? '?'}` : ''), {
    icon: HashIcon,
    align: 'right',
    sortValue: (r) => (r.data && r.data.batch && r.data.batch.fresh) || 0,
    render: (r) => (r.data && r.data.batch ? <Mono>{`${r.data.batch.fresh ?? '?'} / ${r.data.batch.carried ?? '?'}`}</Mono> : <span className="text-faint">—</span>),
  }),
  numCol('calls', 'Calls', (r) => countOf(r.data && r.data.calls)),
  numCol('waiting', 'Waiting', (r) => countOf(r.data && r.data.waiting)),
  numCol('manual', 'Manual', (r) => countOf(r.data && r.data.manualReply)),
  numCol('tasks', 'Tasks', (r) => countOf(r.data && r.data.tasks)),
  col('localDay', 'Browser day', (r) => (r.data && r.data.localDay) || '', { icon: CalendarIcon, defaultHidden: true }),
]

const SIGNIN_COLUMNS = [
  whenCol('at', 'At', (r) => r.createdAt),
  col('actor', 'Actor', (r) => r.actorName || r.actor || '', {
    icon: UserIcon,
    render: (r) => (
      <div>
        <div className="font-semibold">{r.actorName || r.actor}</div>
        {r.actorName && <div className="mt-[2px] text-[12px] text-faint">{r.actor}</div>}
      </div>
    ),
  }),
  col('detail', 'Detail', (r) => r.detail || '', { icon: TextIcon }),
]

// ── detail registry ─────────────────────────────────────────────────────────
// `ledger` details read rows from the events ledger (by type); `hs` details
// read metrics.rows[<list>]; `days` is both. A detail page opens straight onto
// its rows: the paragraph that explained each table, and its "Source:" line,
// were taken off every detail page on request (2026-10-11).
// `rowDetail` is what pressing a row opens: 'call' the call itself
// (CallView, ?call=); 'thread' the company's whole history - every email both
// ways with its body, notes, meetings, tasks, deals - the same view the lead
// page opens under View thread (LeadTimelineView, ?thread=). Added for every
// HubSpot company table on request (2026-10-12).
// The row lists the page reads from /metrics: the server's defaults plus the
// opt-in `sends` behind the delivery and open tiles.
const METRICS_ROWS = 'outreach,followups,replies,responses,deals,tasks,calls,notes,meetings,sends'

const DETAILS = {
  days: {
    title: 'Days ledger',
    columns: DAY_COLUMNS,
    rowKey: (r) => r.day,
    defaultSort: { key: 'day', dir: 'desc' },
    searchKeys: ['day'],
  },
  // THE STREAK TILES GET THEIR OWN TABLE, because they are the only two tiles
  // on the page whose number is not computed from the selected range. Sending
  // them to `days` put a "4 days" tile above a one-row table on the default
  // range, and made the "Longest streak" note name two dates that were not in
  // it — breaking the page's own promise that every number opens the rows
  // behind it, on the two numbers most likely to be doubted.
  streak: {
    title: 'Streak ledger',
    columns: STREAK_DAY_COLUMNS,
    rowKey: (r) => r.day,
    defaultSort: { key: 'day', dir: 'desc' },
    searchKeys: ['day'],
  },
  questions: { title: 'Questions asked', type: 'question_asked', columns: QUESTION_COLUMNS, searchValue: (r) => `${(r.data && r.data.preview) || ''} ${r.detail || ''}` },
  copies: { title: 'Drafts copied', type: 'draft_copied', columns: COPY_COLUMNS, searchValue: (r) => `${(r.data && r.data.company) || ''} ${(r.data && r.data.email) || ''} ${(r.data && r.data.kind) || ''}` },
  hubspot_opens: { title: 'HubSpot opened', type: 'hubspot_email_opened', columns: OPEN_COLUMNS, searchValue: (r) => `${(r.data && r.data.company) || ''} ${(r.data && r.data.kind) || ''}` },
  outreach: {
    title: 'Outreach emails',
    hs: 'outreach',
    rowDetail: 'thread',
    columns: OUTREACH_COLUMNS,
    searchKeys: ['company_name', 'subject', 'to'],
  },
  followups: { title: 'Follow-ups sent', hs: 'followups', rowDetail: 'thread', columns: FOLLOWUP_COLUMNS, searchKeys: ['company_name', 'subject', 'to'] },
  replies: { title: 'Replies sent', hs: 'replies', rowDetail: 'thread', columns: REPLY_COLUMNS, searchKeys: ['company_name', 'subject', 'to'] },
  // `leadLinks` is what makes the page read the leads directory for this
  // table — see the loader in the component. Set it on any detail whose
  // columns include leadCol(), and on no other, or a table with no Lead column
  // pays for the whole directory to draw nothing.
  responses: { title: 'Responses', hs: 'responses', rowDetail: 'thread', leadLinks: true, columns: RESPONSE_COLUMNS, searchKeys: ['company_name', 'subject', 'from', 'snippet'] },
  deals: { title: 'Deals', hs: 'deals', rowDetail: 'thread', columns: DEAL_COLUMNS, searchKeys: ['name', 'company_name', 'stage'] },
  tasks: { title: 'HubSpot tasks', hs: 'tasks', rowDetail: 'thread', columns: TASK_COLUMNS, searchKeys: ['subject', 'company_name', 'status'] },
  calls: {
    title: 'Calls made',
    hs: 'calls',
    columns: CALL_COLUMNS,
    // Pressing a row opens the call itself (CallView): notes, HubSpot's AI
    // summary, the numbers, and the link to the recording and transcript.
    rowDetail: 'call',
    searchKeys: ['company_name', 'title', 'body', 'disposition', 'outcome', 'source'],
  },
  meetings: {
    title: 'Meetings',
    hs: 'meetings',
    rowDetail: 'thread',
    columns: MEETING_COLUMNS,
    searchKeys: ['company_name', 'title', 'body', 'outcome'],
  },
  notes: {
    title: 'Notes written',
    hs: 'notes',
    rowDetail: 'thread',
    columns: NOTE_COLUMNS,
    searchKeys: ['company_name', 'body'],
  },
  sends: {
    title: 'Emails sent',
    hs: 'sends',
    rowDetail: 'thread',
    columns: SEND_COLUMNS,
    searchKeys: ['company_name', 'subject', 'to', 'status', 'source', 'bounce'],
  },
  runs: { title: 'Operation runs', type: 'operation_run', columns: RUN_COLUMNS, searchValue: (r) => OPERATION_LABELS[(r.data && r.data.operationId)] || '' },
  signins: { title: 'Sign-ins', type: 'sign_in', columns: SIGNIN_COLUMNS, searchKeys: ['actor', 'actorName', 'detail'] },
  // NOT A TABLE. The calendar is the page that holds the two charts which used
  // to close the summary a long scroll under the tiles — the activity density
  // and the day timeline (moved 2026-09-11, user request: the summary is the
  // tiles, the day is its own page). Opened from the toolbar's View calendar
  // button, it slides in and back exactly like a tile's rows. No columns, no
  // rows, no export; the overlay branches on `view`.
  calendar: { title: 'Calendar', view: 'calendar' },
  // TWO PAGES, NOT ONE (2026-10-13, user: "split activity density and
  // calendar into two buttons and two pages"). The calendar page used to hold
  // both charts; now `calendar` is the day timeline alone and `density` is the
  // activity-density chart alone. Each has its own toolbar button, and both
  // keep the date row (range chips + custom dates), because both are about
  // which days. `view` is what the overlay branches on; `via=<key>` is how a
  // table opened from one of them finds its way back.
  density: { title: 'Activity density', view: 'density' },
}

export default function Activity() {
  const [searchParams, setSearchParams] = useSearchParams()
  const member = String(searchParams.get('member') || '').toLowerCase()
  // ?range=<key>, plus ?from=YYYY-MM-DD&to=YYYY-MM-DD when the key is 'custom'.
  // An unreadable key falls back to today rather than to a wide window: a typo
  // in a shared link must never quietly widen the claim the link was making.
  const rangeParam = searchParams.get('range')
  const rangeKey = isRangeKey(rangeParam) ? rangeParam : DEFAULT_RANGE_KEY
  const fromParam = searchParams.get('from') || ''
  const toParam = searchParams.get('to') || ''
  // ?density=hour|day — which SHAPE of the activity-density chart the reader
  // asked for, which is not the same question as the range. It is resolved
  // against the range a few lines down rather than here (densityViewOf),
  // because what the page opens on when the param is absent depends on how
  // many days the range spans: one day can only answer "which hours", every
  // longer window opens on days. Anything unreadable falls back the same way,
  // so a link carrying ?density=week cannot leave the toggle pointing at a
  // state no button on the page can undo.
  const densityParam = searchParams.get('density')
  // Own keys only: DETAILS is a plain object, so a truthiness check hands
  // ?detail=toString an inherited function as the config and the overlay then
  // reads .columns[0] off undefined — a blank page from a URL that parses.
  const detailParam = searchParams.get('detail') || ''
  const detail = Object.prototype.hasOwnProperty.call(DETAILS, detailParam) ? detailParam : ''
  const ownerParam = searchParams.get('owner') // null = not chosen yet
  const addressesParam = searchParams.get('addresses') // null = not chosen yet
  // ?call= — the one call the calls detail is reading (CallView); ?thread= —
  // the row whose company history a HubSpot table is showing (LeadTimelineView);
  // ?day= — the day the timeline is on, clamped to the range (timelineDayOf).
  const callParam = String(searchParams.get('call') || '').trim()
  const threadParam = String(searchParams.get('thread') || '').trim()
  const dayParam = searchParams.get('day')
  // ?via=calendar — the table under this detail was opened from a block on the
  // calendar page, so Back returns to the calendar rather than past it to the
  // summary. Anything else is nothing.
  // Any chart page (a DETAILS entry with a `view`) can be where a table was
  // opened from; anything else is nothing.
  const viaRaw = searchParams.get('via') || ''
  const viaParam = Object.prototype.hasOwnProperty.call(DETAILS, viaRaw) && DETAILS[viaRaw].view ? viaRaw : ''

  // OPENING OR CLOSING A DETAIL PUSHES; EVERYTHING ELSE REPLACES.
  //
  // The detail is a page — it slides in over the summary and has a Back button
  // — so it has to occupy its own history entry, or the browser's own Back
  // (Cmd+[, Alt+←, the mouse's back button) skips straight past Activity to
  // whatever was open before it. That is exactly what happened: from an
  // evidence table, Back landed on Deal Flow.
  //
  // The other params are not pages. Changing the member, the range, the HubSpot
  // identity or the sending addresses adjusts the view in place, often several
  // times in a row, and pushing each one would bury the way out under a dozen
  // near-identical entries. Those keep replacing.
  const setParam = useCallback(
    (patch) => {
      // Opening a detail from the summary, or closing one, is a page move and
      // PUSHES. Switching straight from one detail to another is not a new
      // depth — it replaces, so Back from the second still returns to the
      // summary rather than walking back through every table that was opened.
      // Decided HERE, from the params currently rendered, because the options
      // argument is evaluated before setSearchParams ever runs the updater —
      // a flag set inside the updater would always arrive one change late.
      const changesDetail = Object.prototype.hasOwnProperty.call(patch, 'detail')
      const wasOpen = Boolean(searchParams.get('detail'))
      const willOpen = changesDetail ? Boolean(patch.detail) : wasOpen
      const push = changesDetail && wasOpen !== willOpen
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          for (const [k, v] of Object.entries(patch)) {
            if (v === null || v === undefined || v === '') next.delete(k)
            else next.set(k, String(v))
          }
          return next
        },
        { replace: !push },
      )
    },
    [setSearchParams, searchParams],
  )

  const [members, setMembers] = useState(null) // null = loading
  const [owners, setOwners] = useState(null)
  const [tick, setTick] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const cache = useRef(new Map())
  // Row-read requests currently on the wire, by cache key — the main effect
  // and the open-detail effect can both ask for the same eager type in one
  // render (e.g. Refresh with the runs detail open), and a listEventsRange
  // walk can take 20 s; there is no reason to run it twice in parallel.
  const inflight = useRef(new Set())

  const range = useMemo(() => rangeFor(rangeKey, now, { from: fromParam, to: toParam }), [rangeKey, fromParam, toParam, now])
  // 'hour' or 'day' — the view the density toggle is on, and the value sent to
  // the mirror as ?density_bucket=. The page cannot compute this view from
  // another one: a browser can sum hours into days and can NEVER split days or
  // weeks back into hours, so the toggle is a request to the server and not a
  // re-render of what is already here. Whether the answer came back in the
  // bucket that was asked for is a separate question, asked below.
  const densityView = densityViewOf(densityParam, range)
  // True only for a range that is one Chicago day wide. 'Hourly' means two
  // different charts either side of this line — one day's real hours, or the
  // hour-of-day profile summed across every day of the range — and the toggle's
  // tooltip has to say which one the reader would get.
  const singleDayRange = Boolean(range.sinceDay) && range.sinceDay === range.untilDay
  // 'custom' is a MODE, not only a resolved range: when the from/to in the link
  // are unusable rangeFor honestly resolves to today (and says so in
  // range.notice), but the picker has to stay on screen with the dates the
  // reader typed, or the correction happens behind their back.
  const customMode = rangeKey === 'custom'
  const todayRange = useMemo(() => rangeFor('today', now), [now])
  const todayDay = todayRange.untilDay

  // ONE READ OR TWO. The streak window always ends today, so it can double as
  // the range's own operation_run read only when the range ends today too
  // (runsPlan.merged): then one request covers both and the range rows are
  // filtered back out below. When the range is in the PAST, merging would
  // stretch the read from the range's first day all the way to today — a
  // 31-day custom window in 2020 becomes a 2372-day request that the ledger's
  // backwards paging abandons holding the newest rows, i.e. not the ones the
  // reader picked. So those ranges get two bounded reads instead; see
  // streakWindow in lib/activityMetrics.js, where the arithmetic is pinned by
  // a test rather than buried in JSX.
  const runsPlan = useMemo(() => streakWindow(range, todayDay, STREAK_WINDOW_DAYS), [range, todayDay])
  const runsMerged = runsPlan.merged
  const runsWindow = useMemo(
    () => ({
      sinceDay: runsPlan.sinceDay,
      untilDay: runsPlan.untilDay,
      since: runsPlan.sinceDay ? chicagoMidnightISO(runsPlan.sinceDay) : null,
      // Always the end of today, because streakWindow always ends today.
      until: todayRange.until,
    }),
    [runsPlan, todayRange.until],
  )

  // Cache/identity for a window, keyed by the DAYS it covers rather than by the
  // range key: two keys that resolve to the same days (custom Aug 1–Aug 28 and
  // "this month" on Aug 28) are the same read and should hit the same cache
  // entry, and a key whose days moved (any relative range, once the Chicago day
  // ticks over) must miss it.
  const rangeId = `${range.sinceDay || 'start'}|${range.untilDay}`
  const runsWindowId = `${runsWindow.sinceDay || 'start'}|${runsWindow.untilDay}`

  // ── roster + HubSpot owners (once) ───────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    listMembers()
      .then(({ members: list, mode }) => {
        if (!cancelled) setMembers({ list: Array.isArray(list) ? list : [], mode })
      })
      .catch(() => {
        if (!cancelled) setMembers({ list: [], mode: 'local' })
      })
    hubspotOwners().then((res) => {
      if (!cancelled) setOwners(res)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Default member: ?member= wins; else the single rep; else the first person.
  useEffect(() => {
    if (!members || member) return
    const list = members.list
    // roleOrEmpty, not a raw string test. The invite wizard's own labels are
    // "Admin / Manager / Sales Rep / Viewer", so a roster row saved as
    // "Sales Rep" failed `=== 'rep'`, no rep was found, and this page — the one
    // investors are shown — silently defaulted to whoever happened to be first
    // in the roster instead of the rep whose numbers it claims to display.
    const reps = list.filter((m) => roleOrEmpty(m.role) === 'rep')
    const pick = (reps.length === 1 ? reps[0] : list[0]) || null
    if (pick && pick.email) setParam({ member: String(pick.email).toLowerCase() })
  }, [members, member, setParam])

  const memberRow = useMemo(
    () => (members ? members.list.find((m) => String(m.email || '').toLowerCase() === member) || null : null),
    [members, member],
  )
  const memberName = (memberRow && memberRow.name) || member

  // ── HubSpot identity: URL → remembered → email match → first name → none ──
  const ownerList = useMemo(() => ((owners && owners.owners) || []).filter((o) => o && o.id), [owners])
  const identity = useMemo(() => {
    if (!member) return { ownerId: '', addresses: '', how: 'none' }
    if (ownerParam !== null) {
      // 'none' is the sentinel for the explicit "No HubSpot identity" choice —
      // setParam deletes empty values, so a bare '' could not survive in a
      // shared link and the email match would silently resurrect on another
      // browser.
      const ownerId = ownerParam === 'none' ? '' : ownerParam
      return { ownerId, addresses: addressesParam || '', how: ownerId ? 'chosen' : 'none' }
    }
    const remembered = readIdentity(member)
    if (remembered && typeof remembered.ownerId === 'string') {
      // The URL wins over what this browser remembers: a shared link that says
      // ?addresses= means THOSE addresses, not whatever the recipient's
      // localStorage happens to hold for this member.
      return {
        ownerId: remembered.ownerId,
        addresses: addressesParam !== null ? addressesParam : remembered.addresses || '',
        how: remembered.ownerId ? 'remembered' : 'none',
      }
    }
    const byEmail = ownerList.find((o) => String(o.email || '').toLowerCase() === member)
    if (byEmail) return { ownerId: byEmail.id, addresses: addressesParam || '', how: 'email' }
    const fn = firstName(memberName)
    const byName = fn ? ownerList.find((o) => firstName(o.name) === fn) : null
    if (byName) return { ownerId: byName.id, addresses: addressesParam || '', how: 'first name' }
    return { ownerId: '', addresses: addressesParam || '', how: 'none' }
  }, [member, ownerParam, addressesParam, ownerList, memberName])

  const owner = ownerList.find((o) => o.id === identity.ownerId) || null
  const extraAddresses = useMemo(
    () =>
      String(identity.addresses || '')
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.includes('@')),
    [identity.addresses],
  )

  function chooseIdentity(ownerId, addresses) {
    const addr = addresses === undefined ? identity.addresses : addresses
    writeIdentity(member, { ownerId: ownerId || '', addresses: addr || '' })
    // "No HubSpot identity" goes into the URL as owner=none (see the sentinel
    // note in the identity memo) so the choice survives sharing the link.
    setParam({ owner: ownerId || 'none', addresses: addr || '' })
  }

  // ── ledger reads ─────────────────────────────────────────────────────────
  const [summary, setSummary] = useState({ status: 'idle' })
  const [rowsByType, setRowsByType] = useState({})
  const [metrics, setMetrics] = useState({ status: 'idle' })

  const rangeArgs = useMemo(
    () => ({ since: range.since || undefined, until: range.until, actor: member }),
    [range.since, range.until, member],
  )
  // The streak's window (see runsWindow). When the range ends today this IS
  // the operation_run read and one request serves both — the rows outside the
  // range are filtered out below for the tiles and the Runs table, and kept
  // for the streak. When it does not, RUNS_STREAK_TYPE is read with these args
  // and plain 'operation_run' falls back to rangeArgs like every other type.
  const runsArgs = useMemo(
    () => ({ since: runsWindow.since || undefined, until: runsWindow.until, actor: member }),
    [runsWindow.since, runsWindow.until, member],
  )
  const argsForType = useCallback(
    (type) => (type === RUNS_STREAK_TYPE || (type === 'operation_run' && runsMerged) ? runsArgs : rangeArgs),
    [runsArgs, rangeArgs, runsMerged],
  )
  const keyForType = useCallback(
    (type) =>
      `${member}|${type === RUNS_STREAK_TYPE || (type === 'operation_run' && runsMerged) ? runsWindowId : rangeId}|${type}`,
    [member, runsMerged, runsWindowId, rangeId],
  )
  // The logical type the streak ledger reads from: the merged operation_run
  // read when there is one, its own read otherwise.
  const streakType = runsMerged ? 'operation_run' : RUNS_STREAK_TYPE
  // The eager reads for THIS range. The second operation_run read is only
  // fetched when the windows could not be merged, so a today-ending range
  // (which is every default page load) still makes exactly three requests.
  const eagerTypes = useMemo(
    () => [...(runsMerged ? EAGER_TYPES : [...EAGER_TYPES, RUNS_STREAK_TYPE]), ...TIMELINE_EXTRA_TYPES],
    [runsMerged],
  )

  const loadType = useCallback(
    async (type) => {
      const key = keyForType(type)
      if (cache.current.has(key)) {
        setRowsByType((prev) => (prev[type] && prev[type].key === key ? prev : { ...prev, [type]: cache.current.get(key) }))
        return
      }
      if (inflight.current.has(key)) {
        // Already being fetched — just make sure the placeholder carries THIS
        // key so the guarded write below lands the response when it arrives.
        setRowsByType((prev) =>
          prev[type] && prev[type].key === key ? prev : { ...prev, [type]: { key, status: 'loading', events: [] } },
        )
        return
      }
      inflight.current.add(key)
      setRowsByType((prev) => ({ ...prev, [type]: { key, status: 'loading', events: [] } }))
      // The ledger type to ask for, which is not always the logical type:
      // RUNS_STREAK_TYPE is a second window over 'operation_run' rows.
      const eventType = eventTypeOf(type)
      let res = null
      try {
        // listEventsRange fails soft (mode:'local'), so this catch is belt and
        // braces — but a rejection here must not leave the placeholder
        // spinning forever with the inflight key stuck.
        res = await listEventsRange({ ...argsForType(type), types: [eventType] })
      } catch {
        res = null
      } finally {
        inflight.current.delete(key)
      }
      const ok = res && res.mode === 'api'
      const entry = {
        key,
        status: ok ? 'ok' : 'error',
        events: ok ? res.events.filter((e) => e.type === eventType) : [],
        complete: ok ? res.complete !== false : false,
        httpStatus: res && res.status,
      }
      if (ok) cache.current.set(key, entry)
      // Only land the response if this request is still the one the page is
      // waiting on. The 'loading' placeholder above carries the request key;
      // if the viewer switched member or range while this fetch was in flight,
      // the placeholder was replaced and prev[type].key no longer matches —
      // dropping the write here is what keeps member A's rows from rendering
      // under member B's name.
      setRowsByType((prev) => (prev[type] && prev[type].key === key ? { ...prev, [type]: entry } : prev))
    },
    [keyForType, argsForType],
  )

  useEffect(() => {
    if (!member) return
    let cancelled = false
    const key = `${member}|${rangeId}|summary`
    if (cache.current.has(key)) setSummary(cache.current.get(key))
    else {
      setSummary({ status: 'loading' })
      eventsSummary(rangeArgs).then((res) => {
        if (cancelled) return
        const ok = res && res.mode === 'api'
        const entry = { status: ok ? 'ok' : 'error', data: ok ? res : null, httpStatus: res && res.status, complete: ok ? res.complete !== false : false }
        if (ok) cache.current.set(key, entry)
        setSummary(entry)
      })
    }
    for (const t of eagerTypes) loadType(t)
    return () => {
      cancelled = true
    }
  }, [member, rangeId, rangeArgs, eagerTypes, loadType, tick])

  // A detail that needs ledger rows loads them when opened. `tick` is a dep so
  // Refresh re-reads an OPEN detail too: refresh() clears the cache, and on
  // the same Chicago day nothing else in this effect's inputs changes, so
  // without it the open questions/sign-ins table would keep its stale rows
  // despite the button's promise to re-read the ledger.
  useEffect(() => {
    const cfg = DETAILS[detail]
    if (cfg && cfg.type && member) loadType(cfg.type)
  }, [detail, member, loadType, tick])

  // ── HubSpot metrics ───────────────────────────────────────────────────────
  // ── the daily report ───────────────────────────────────────────────────────
  // "Send daily report": today's seven HubSpot tiles as a one-page PDF, mailed
  // by the mirror lambda (POST /api/hubspot/daily-report) to the address it is
  // configured with. Always TODAY, whatever range the page is showing — the
  // button is the morning-after summary, not an export of the current view.
  // The lambda reads the same feeds the tiles come from, so the PDF's Today
  // column IS the tile numbers, with yesterday and four per-weekday averages
  // beside each. The reply names the recipient and the clock time, and the
  // line under the toolbar says so — or says exactly why nothing went out.
  // One send at a time: the button disables while a request is in flight.
  const [report, setReport] = useState({ status: 'idle', text: '' })
  const sendReport = useCallback(async () => {
    if (!member || report.status === 'sending') return
    setReport({ status: 'sending', text: '' })
    const res = await hubspotDailyReport({
      actor: member,
      addresses: extraAddresses.join(','),
      owner_ids: identity.ownerId,
      member_name: memberName,
    })
    if (res && res.emailed) {
      const to = (res.to || []).join(', ')
      setReport({
        status: 'sent',
        text: `Daily report for ${res.day_label || 'today'} sent to ${to || 'the configured address'}${res.as_of ? ` at ${res.as_of}` : ''}.`,
      })
    } else {
      setReport({ status: 'error', text: (res && res.reason) || 'The daily report could not be sent.' })
    }
  }, [member, report.status, extraAddresses, identity.ownerId, memberName])

  useEffect(() => {
    if (!member) return
    let cancelled = false
    const addr = extraAddresses.join(',')
    // THE DENSITY VIEW IS PART OF THE KEY, because it is part of the REQUEST:
    // the same member over the same range answers with hourly bars or daily
    // ones depending on ?density_bucket=, and a cache keyed without it would
    // serve the previous bucket's bars under the new label — the wrong chart,
    // correctly drawn, which is the hardest kind of wrong to notice.
    const key = `${member}|${rangeId}|${identity.ownerId}|${addr}|${densityView}|metrics`
    if (cache.current.has(key)) {
      setMetrics(cache.current.get(key))
      return undefined
    }
    setMetrics({ status: 'loading' })
    hubspotMetrics({
      actor: member,
      addresses: addr,
      owner_ids: identity.ownerId,
      since: range.sinceDay || '',
      until: range.untilDay,
      // `sends` (every send with its opens/clicks/bounce) is served only when
      // named; the rest is the server's default list. A mirror lambda one
      // deploy behind drops the name it does not know and answers the rest.
      rows: METRICS_ROWS,
      // The bucket the READER asked for. A mirror one deploy behind ignores it
      // and keeps choosing from the span of the range; that answer is not
      // wrong, it is an answer to a different question, and the chart says so
      // rather than drawing it (densityViewMismatchNote).
      density_bucket: densityView,
    }).then((res) => {
      if (cancelled) return
      const usable = res && res.available && res.configured !== false && res.complete !== false && res.totals
      const entry = { status: usable ? 'ok' : 'error', data: res, reason: usable ? '' : metricsFailure(res) }
      if (usable) cache.current.set(key, entry)
      setMetrics(entry)
    })
    return () => {
      cancelled = true
    }
  }, [member, rangeId, identity.ownerId, extraAddresses, range.sinceDay, range.untilDay, densityView, tick])

  // MIDNIGHT ROLLOVER. `now` is otherwise frozen until Refresh, which was
  // harmless when the page opened on a 30-day window but is not now that it
  // opens on TODAY: a tab left open overnight would go on labelling yesterday's
  // numbers "Today" until someone pressed a button. Polling the Chicago day
  // once a minute is enough — the state only changes when the day key actually
  // ticks over, and then every window, cache key and label moves with it. The
  // cache is left alone: its keys carry the days, so yesterday's entries simply
  // stop being asked for.
  useEffect(() => {
    const id = setInterval(() => {
      const at = Date.now()
      if (chicagoDayOf(at) !== todayDay) setNow(at)
    }, 60000)
    return () => clearInterval(id)
  }, [todayDay])

  function refresh() {
    cache.current.clear()
    // Forget what is on the wire too: Refresh promises a re-read, not the
    // answer to a request that started before the button was pressed.
    inflight.current.clear()
    setNow(Date.now())
    setTick((t) => t + 1)
  }

  // ── derived ──────────────────────────────────────────────────────────────
  // Belt and braces on top of the guarded write in loadType: never hand out
  // rows fetched for a different member or range, even if a stale entry
  // somehow survived in state.
  const rowsOf = (type) => {
    const r = rowsByType[type]
    return r && r.status === 'ok' && r.key === keyForType(type) ? r.events : null
  }
  // IS THIS READ STILL COMING, or is it never coming? rowsOf() collapses both
  // into null, and a tile cannot tell the two apart — which is how a number
  // that had not arrived yet rendered as the same em dash this page uses for
  // "unavailable". Absent, still walking, or keyed to a member/range the user
  // has already moved off (a fresh read is in flight for the new key) all mean
  // WAITING. Only status 'error' means the answer is not coming.
  const rowsLoading = (type) => {
    const r = rowsByType[type]
    return !r || r.status === 'loading' || r.key !== keyForType(type)
  }
  // Read over the streak window (runsWindow), which always ends today. When
  // the range ends today too this is the same read as the range's, widened;
  // otherwise it is the separate RUNS_STREAK_TYPE read. Everything that
  // reports on the RANGE uses `runs` below.
  const runsWide = rowsOf(streakType)
  const inSelectedRange = useCallback(
    (at) => {
      const d = chicagoDayOf(at)
      return Boolean(d) && (!range.sinceDay || d >= range.sinceDay) && d <= range.untilDay
    },
    [range.sinceDay, range.untilDay],
  )
  // The range's own operation-run rows. In the merged case this trims the
  // streak's extra days off the front; in the unmerged case the read was
  // already the range and the filter is belt and braces.
  const runsRange = rowsOf('operation_run')
  const runs = useMemo(() => (runsRange ? runsRange.filter((r) => inSelectedRange(r.createdAt)) : null), [runsRange, inSelectedRange])
  const copies = rowsOf('draft_copied')
  const opens = rowsOf('hubspot_email_opened')
  const questions = rowsOf('question_asked')
  const signins = rowsOf('sign_in')
  const metricsData = metrics.status === 'ok' ? metrics.data : null
  const summaryData = summary.status === 'ok' ? summary.data : null

  const days = useMemo(
    () =>
      buildDays({
        summaryDays: (summaryData && summaryData.days) || {},
        metrics: metricsData,
        runs: runs || [],
        copies,
        opens,
        sinceDay: range.sinceDay,
        untilDay: range.untilDay,
        now,
      }),
    [summaryData, metricsData, runs, copies, opens, range.sinceDay, range.untilDay, now],
  )

  // The ledger the STREAK is counted over — the same rows, over the wider
  // window, with no HubSpot columns because a streak only reads the ops.
  const streakDays = useMemo(
    () => buildDays({ runs: runsWide || [], sinceDay: runsWindow.sinceDay, untilDay: runsWindow.untilDay, now }),
    [runsWide, runsWindow.sinceDay, runsWindow.untilDay, now],
  )

  const s = useMemo(
    () => summarize({ days, streakDays, now, summary: summaryData, copies, opens, metrics: metricsData, runs: runs || [] }),
    [days, streakDays, now, summaryData, copies, opens, metricsData, runs],
  )

  // ── activity density ─────────────────────────────────────────────────────
  //
  // WHEN THE WORK HAPPENS, off the HubSpot mirror. The mirror buckets the
  // stamped engagements and ships them on the metrics body; WHICH bucket is now
  // the reader's choice, sent as ?density_bucket= and shown by the Hourly/Daily
  // toggle in the card's header. A mirror that has not been redeployed ignores
  // the parameter and keeps picking from the span of the range (hourly for a
  // single day, daily for a short range, Monday-start weeks for a long one),
  // which is a real answer to a different question and is said out loud rather
  // than drawn — see densityMismatch below. The block itself is built by a
  // lambda that deploys on its own schedule, so a mirror one deploy behind can
  // simply answer without it. normalizeDensity turns that, and every
  // half-written variant of it, into either something drawable or null — and
  // null prints "unavailable", never an empty chart.
  const density = useMemo(() => normalizeDensity(metricsData && metricsData.density), [metricsData])
  // ONE FACT THE PAGE HAS TO SAY RATHER THAN DRAW, read off the RAW block
  // because normalizeDensity keeps only what it can put in a bar: `capped`, the
  // range asked for more buckets than the server will draw and it clipped to the
  // most recent of them, so the bars are PART of the range. Being told is the
  // reader's only way to know.
  const densityBlock = (metricsData && metricsData.density) || null
  const densityCapped = Boolean(densityBlock && densityBlock.capped === true)
  // THE PAYLOAD THAT IS ACTUALLY DRAWN. The mirror's own, normally — but a
  // mirror that READ the range and found nothing in it sends no points, and
  // this page no longer answers that with a line of prose standing where the
  // chart should be. A reader who asked about a quiet week deserves to see the
  // week. densityAxis synthesises the bars for exactly that case, every one of
  // them a real zero, drawn exactly like a range full of activity. null back
  // from it means there is no axis to build — all time has no first day to
  // count from — and the sentence is then the only thing left to print.
  //
  // The fallback is only ever ON SCREEN when both note paths below are empty:
  // an axis of zeros is the honest answer where the range is KNOWN to be empty,
  // and never where the read failed or the mirror answered another bucket.
  const densityChart = useMemo(() => {
    if (!density) return null
    if (density.points.length) return density
    return densityAxis({ view: densityView, sinceDay: range.sinceDay, untilDay: range.untilDay })
  }, [density, densityView, range.sinceDay, range.untilDay])
  // THE BUCKET IN PROGRESS IS NOT A SLUMP. Whenever the range runs to today
  // one bucket is minutes old and the ones after it have not happened; both
  // would otherwise read as a rep who stopped working. Which bar that is comes
  // from the points' own start instants, not from "the last one" — a chart of
  // today's hours may carry all 24 so the axis is a whole day. Said in the
  // affected bars' tooltips, and the bars after it carry the faint not-yet band.
  // An hour-of-day profile has no bar in progress at all and densityFillingIndex
  // says so: 11pm holds every 11pm in the range, nearly all of them on days that
  // closed long ago. These three read the DRAWN payload, so a synthesised axis
  // of zeros gets the same marker, the same thinning and the same band as a real one.
  const densityFilling = useMemo(
    () => densityFillingIndex(densityChart, { endsToday: range.untilDay === todayDay, now }),
    [densityChart, range.untilDay, todayDay, now],
  )
  const densityData = useMemo(() => densityBars(densityChart, { fillingAt: densityFilling }), [densityChart, densityFilling])
  const densityPlan = useMemo(() => densityLabelPlan(densityChart), [densityChart])
  // WHAT THE CHART COUNTS, IN THE PAYLOAD'S OWN WORDS. The heading used to
  // name the kinds itself — "emails, calls, meetings, notes, tasks and deals"
  // — which is a promise this page cannot keep: the block is built by a lambda
  // that chooses its own kinds, and the mirror holds tasks and deals as row
  // sets the engagement feed does not carry, so the sentence could name work
  // that is in no bar. Built from `density.kinds` it can only ever say what
  // was actually counted, which is the rule densityBreakdown already
  // follows — and it is where "emails sent" versus
  // "emails" belongs, because only the mirror knows whether it dropped the
  // inbound ones. Same for the clock: named when the payload names it, not
  // assumed to be Chicago.
  const densityKinds = density && density.kinds.length ? density.kinds.map((k) => k.label).join(', ') : ''
  const densityTitle = [
    densityKinds
      ? `Counted by kind: ${densityKinds}.`
      : 'Whatever this HubSpot mirror timestamps for this member — it did not name the kinds it counted.',
    `Each one sits in the bucket the mirror timestamped it in${density && density.tz ? `, on the ${density.tz} clock` : ''} — the hour it was LOGGED in HubSpot, which for someone working offline and syncing later, or from another zone, is not the hour they were at their desk.`,
    'Hover a bar for the breakdown by kind.',
  ].join(' ')
  // WHAT EACH SEGMENT MEANS — and above all what 'Hourly' means over more than
  // one day, which is the one thing about this control a reader cannot work out
  // by pressing it. Over a multi-day range Hourly is NOT a timeline: it is 24
  // bars, one per hour of the clock, in which every 9 AM in the window is summed
  // into the same bar. Read as a timeline it turns a month of work into a single
  // Tuesday, so the tooltip says which chart the segment gives before it is
  // pressed. Neither segment is ever disabled: a view the mirror cannot answer
  // explains itself under the toggle, in a sentence a reader can act on, where a
  // greyed-out button says nothing at all.
  const densityViewTitle = (view) =>
    view === 'day'
      ? 'Daily: one bar per America/Chicago day in the range.'
      : singleDayRange
        ? 'Hourly: one bar per hour of this day, in the order the hours happened.'
        : 'Hourly: the hour-of-day profile — 24 bars, one per hour of the clock, with every 9 AM in the range summed into the same bar. Which hours the work lands in, not a timeline.'

  const ledgerOk = summary.status === 'ok'
  const ledgerLoading = summary.status === 'loading' || members === null
  const routineOk = ledgerOk && runs !== null
  const draftsOk = ledgerOk && copies !== null && opens !== null
  // WAITING IS NOT THE SAME FACT AS UNAVAILABLE, and the tiles must not say
  // the same thing for both. Each group waits on its own reads, so each keeps
  // its own flag rather than sharing one page-wide spinner: the ledger can be
  // in while the mirror is still walking, and half a page of real numbers
  // beats a page of dashes.
  const routineLoading = ledgerLoading || rowsLoading('operation_run')
  const draftsLoading = ledgerLoading || rowsLoading('draft_copied') || rowsLoading('hubspot_email_opened')
  // An eager row read that stopped early (20 s budget) poisons the tiles
  // DERIVED from those rows — the streak, "Days all 3 ran", the uniques under
  // "Working the drafts", the runs tile — not just its own detail table, so the warning has
  // to sit on the page, not only inside an opened detail.
  const incompleteEager = eagerTypes.filter((t) => {
    const r = rowsByType[t]
    return r && r.status === 'ok' && r.key === keyForType(t) && r.complete === false
  })
  const resultsOk = metrics.status === 'ok'
  const resultsLoading = metrics.status === 'loading'
  // WHAT STANDS IN THE DENSITY CHART'S PLACE, IN PRECEDENCE ORDER. '' from
  // both of these means draw the chart.
  //
  //   densityNote      the read failed, the mirror shipped no density block, or
  //                    it counted activity it then placed in no bucket. Every
  //                    one of those is something we were NOT TOLD, and bars of
  //                    zeros for it are exactly the lie this page refuses. A
  //                    range the mirror read and found EMPTY is no longer one of
  //                    them: that one is known, so it gets a chart of real zeros.
  //   densityMismatch  the mirror answered in a bucket nobody asked for —
  //                    because it is a deploy behind, or because the previous
  //                    view's answer is still on screen while the new one is
  //                    read. Unavailable, not zero: the bars would be real, the
  //                    axis would be real, and the chart would be answering a
  //                    question the reader did not ask with nothing on screen to
  //                    give it away. Computed as '' whenever densityNote already
  //                    took the chart, so the precedence lives here rather than
  //                    being re-decided in the JSX.
  const densityNote = densityEmptyNote(density, { resultsOk, loading: metrics.status === 'loading' })
  const densityMismatch = densityNote ? '' : densityViewMismatchNote(density, densityView)
  // THE CAPTION OVER THE CHART — only what a bar cannot say about itself, and
  // only when it is true, so an ordinary chart (a quiet one included) carries no
  // text at all. densityZeroCaption names activity the mirror counted but could
  // not put on the clock. The capped sentence is the other one: bars that are the
  // most recent PART of the range read as the whole of it unless the reader is
  // told, which is the same lie as a zero nobody computed, only harder to spot.
  const densityCaption = [
    densityZeroCaption(density),
    densityCapped
      ? 'This range holds more buckets than the mirror will draw, so these bars are its most recent part — activity older than the first bar is in none of them.'
      : '',
  ]
    .filter(Boolean)
    .join(' ')
  const outbox = useMemo(() => {
    try {
      return eventOutboxSize() || 0
    } catch {
      return 0
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, summary.status])

  const firstAt = summaryData && summaryData.first_at
  const lastSync = metricsData && metricsData.last_sync_at ? syncAge(metricsData.last_sync_at, now) : null
  const impersonatedCount = s.tracked.impersonatedEvents

  // ── the open detail ──────────────────────────────────────────────────────
  // ── day timeline ─────────────────────────────────────────────────────────
  //
  // WHAT THE DAY LOOKED LIKE, MINUTE BY MINUTE. The density chart above says
  // which hours were busy; this lays one day out like a calendar — every
  // call as tall as it lasted, every meeting as its span, every sent email,
  // note, task, deal and app event at its minute — and measures the idle
  // stretches between them, which is the thing a bar per hour cannot show.
  // Built from rows the page already holds (the mirror's ledger rows and
  // the ledger events), so it costs no extra read beyond the two ledger
  // types added to the eager set. The day defaults to the range's last day
  // (today, for every range that ends today) and ?day= walks the range.
  const timelineDay = timelineDayOf(dayParam, range)
  const ledgerStateOf = (type) => (rowsOf(type) ? 'ok' : rowsLoading(type) ? 'loading' : 'unavailable')
  // Serialised so the memo below keys on the STATES, not on a fresh object.
  const timelineSourcesKey = JSON.stringify({
    hubspot: resultsOk ? 'ok' : resultsLoading || metrics.status === 'idle' ? 'loading' : 'unavailable',
    ledger: {
      runs: ledgerStateOf('operation_run'),
      questions: ledgerStateOf('question_asked'),
      copies: ledgerStateOf('draft_copied'),
      opens: ledgerStateOf('hubspot_email_opened'),
      signins: ledgerStateOf('sign_in'),
    },
  })
  const timeline = useMemo(
    () =>
      dayTimeline({
        day: timelineDay,
        metrics: resultsOk ? metricsData : null,
        // runsRange, not runs: the day filter is the timeline's own, and in
        // the merged case the wider read is the only one there is.
        events: { runs: runsRange, questions, copies, opens, signins },
        sources: JSON.parse(timelineSourcesKey),
        now,
      }),
    [timelineDay, resultsOk, metricsData, runsRange, questions, copies, opens, signins, timelineSourcesKey, now],
  )
  const timelineSummary = dayTimelineSummary(timeline)
  const timelineTitle = `One day of ${memberName || 'the member'}'s work on the America/Chicago clock — every HubSpot call (as long as it lasted), meeting (its span), email sent, note, task created or completed and deal created, plus every operation run, question asked, draft copied, HubSpot opened and sign-in the app recorded. Hatched bands are stretches of ${GAP_MIN_MINUTES} minutes or more with nothing logged. Inbound email is not the rep working and is left out. Each thing sits at the minute HubSpot or the app stamped it.`
  // Where the timeline's arrows and date field can go — see dayStepPlan: a
  // range wider than a day is walked and stops at its edges, and a one-day
  // range is moved whole, so the calendar page can step through days without
  // a trip back to the summary (user request, 2026-09-16).
  const stepPlan = dayStepPlan(range, timelineDay, todayDay)
  const openTimelineBlock = (block) => {
    const link = block && block.link
    if (!link || !link.detail) return
    // A block is pressed on the calendar page, so the rows it opens remember
    // where they came from (?via=) and their Back leads back to the day.
    setParam({ detail: link.detail, call: link.call || null, thread: null, via: detail === 'calendar' ? 'calendar' : null })
  }

  const detailCfg = DETAILS[detail] || null

  // ── the lead-link index ──────────────────────────────────────────────────
  //
  // Read ONCE, and only for a detail that actually draws a Lead column, because
  // it is the leads directory: the HubSpot match index plus every category's
  // cached rows. The tiles and the charts have no use for it and must not wait
  // on it, so it loads when the Responses table opens and is then kept for the
  // rest of the page's life — reopening the table is free.
  //
  // FAILS SOFT, LOUDLY. hubspotIndex() resolves null for every failure mode and
  // a category whose cache will not decrypt is skipped, so this can finish with
  // a partial directory or none at all. It never throws and never blocks the
  // table; what it costs is links, and each missing link says on hover which
  // kind of missing it is (lib/leadLink.js).
  const [leadIndex, setLeadIndex] = useState({ index: null, loading: false, done: false })
  const wantsLeadIndex = Boolean(detailCfg && detailCfg.leadLinks)
  // THE GUARD IS A REF, NOT THE STATE THIS EFFECT WRITES. Reading
  // leadIndex.loading here and listing it as a dependency is a trap that costs
  // the whole read: setting loading:true re-runs the effect, the re-run tears
  // down the first pass, and the answer lands on a torn-down closure that
  // throws it away — leaving the column on "…" for ever, which reads as a slow
  // directory rather than as a bug. The ref says "a read has already started"
  // without being something the effect depends on.
  const leadIndexStarted = useRef(false)
  useEffect(() => {
    if (!wantsLeadIndex || leadIndexStarted.current) return undefined
    leadIndexStarted.current = true
    setLeadIndex({ index: null, loading: true, done: false })
    ;(async () => {
      let built = null
      try {
        const index = await hubspotIndex()
        if (index) {
          const rows = []
          for (const cat of CATEGORY_KEYS) {
            try {
              const got = await getCachedCompanies(cat)
              if (!got || got.stale || !Array.isArray(got.rows)) continue
              // The category rides on the row because the directory rows do not
              // carry it and the lead page wants it. Safe to write: every call
              // decrypts fresh objects, so nothing shared is being mutated.
              for (const row of got.rows) row._category = cat
              rows.push(...got.rows)
            } catch {
              /* a category whose cache will not open costs its own links, not the table */
            }
          }
          annotateRows(rows, index)
          // The companies the import never had are leads too — the directory
          // shows them (leadsCache does exactly this) and a response from one
          // must not be the one row with no way through.
          const crm = crmLeadRows(index, claimedCompanyIds(rows))
          built = buildLeadLinkIndex(crm.length ? [...rows, ...crm] : rows)
        }
      } catch {
        /* best-effort decoration; the table has already rendered */
      }
      // Landed unconditionally: this index belongs to the PAGE, not to the
      // table that asked for it, so closing the detail mid-read must not throw
      // the answer away — reopening it would otherwise find the ref set, start
      // nothing, and wait for ever.
      setLeadIndex({ index: built, loading: false, done: true })
    })()
    return undefined
  }, [wantsLeadIndex])
  const leadLinkValue = useMemo(
    () => ({ index: leadIndex.index, loading: leadIndex.loading }),
    [leadIndex.index, leadIndex.loading],
  )
  const detailRows = useMemo(() => {
    if (!detailCfg) return { rows: [], status: 'idle', warnings: [] }
    const warnings = []
    if (detail === 'days') {
      if (!ledgerOk) warnings.push(ledgerFailure({ status: summary.httpStatus }))
      if (!resultsOk) warnings.push(metricsFailure(metrics.data) || 'HubSpot results unavailable — the Outreach through Notes columns are blank, not zero.')
      if (runs === null) warnings.push('Operation runs have not loaded, so the operation columns are blank, not empty.')
      return { rows: days, status: ledgerOk ? 'ok' : 'error', warnings }
    }
    // The streak's own ledger: streakDays, not days. It is built from one read
    // (the operation runs over the streak window), so its state is that read's
    // state and nothing else — the summary and the mirror have no say here.
    if (detail === 'streak') {
      const entry = rowsByType[streakType]
      if (!entry || entry.status === 'loading') return { rows: [], status: 'loading', warnings }
      if (entry.status !== 'ok' || runsWide === null) {
        return { rows: [], status: 'error', warnings: [ledgerFailure({ status: entry && entry.httpStatus })] }
      }
      if (entry.complete === false) {
        warnings.push(
          'The operation-run read stopped early (time budget) and drops the OLDEST rows first — days are missing from the far end of this table, so the streak above may be short.',
        )
      }
      return { rows: streakDays, status: 'ok', warnings }
    }
    if (detailCfg.type) {
      const entry = rowsByType[detailCfg.type]
      if (!entry || entry.status === 'loading') return { rows: [], status: 'loading', warnings }
      if (entry.status !== 'ok') return { rows: [], status: 'error', warnings: [ledgerFailure({ status: entry.httpStatus })] }
      if (entry.complete === false) warnings.push('The ledger read stopped early (time budget) — this list is incomplete and the counts above may be short.')
      // Operation runs were fetched over the wider streak window; the table is
      // the evidence for the tiles, and the tiles count the RANGE, so the extra
      // days are dropped here rather than padding a list the numbers exclude.
      const scoped = detailCfg.type === 'operation_run' ? entry.events.filter((e) => inSelectedRange(e.createdAt)) : entry.events
      const own = scoped.filter((e) => !isImpersonated(e))
      const imp = scoped.length - own.length
      if (imp) warnings.push(`${imp.toLocaleString()} row${imp === 1 ? '' : 's'} logged while the owner was impersonating this member are excluded.`)
      return { rows: own, status: 'ok', warnings }
    }
    if (detailCfg.hs) {
      if (metrics.status === 'loading' || metrics.status === 'idle') return { rows: [], status: 'loading', warnings }
      if (!resultsOk) return { rows: [], status: 'error', warnings: [metrics.reason || metricsFailure(metrics.data)] }
      const list = (metricsData.rows && metricsData.rows[detailCfg.hs]) || []
      if (metricsData.rows_truncated && metricsData.rows_truncated[detailCfg.hs]) {
        warnings.push('The server trimmed this list to its byte budget (newest first) — the count above is exact, the rows are not all here.')
      }
      return { rows: list, status: 'ok', warnings }
    }
    return { rows: [], status: 'idle', warnings }
  }, [detailCfg, detail, days, streakDays, streakType, runsWide, ledgerOk, resultsOk, runs, rowsByType, metrics, metricsData, summary.httpStatus, inSelectedRange])

  function exportDetail() {
    if (!detailCfg || !detailRows.rows.length) return
    const text = toCsv(
      detailCfg.columns.map((c) => ({ header: typeof c.header === 'string' ? c.header : c.key, value: c.value })),
      detailRows.rows,
    )
    // The filename carries the DAYS for EVERY range, not only custom. Every
    // preset except 'all' moves: two exports named "…-today.csv" taken on
    // different mornings are two different tables with one name, and so are
    // -yesterday, -week, -month and -3m. The key stays in front so the file
    // still says which window was asked for, and the days say which window
    // that turned out to be.
    //
    // The streak ledger is the exception: it names the STREAK window, because
    // that is the window its rows came from. Naming it after the range would
    // put two different tables under one filename again — the bug above.
    const rangeSlug =
      detail === 'streak'
        ? `streakwindow-${runsWindow.sinceDay || 'start'}_${runsWindow.untilDay}`
        : `${range.key}-${range.sinceDay || 'start'}_${range.untilDay}`
    downloadCsv(`${fileSlug(member)}-${fileSlug(detail)}-${fileSlug(rangeSlug)}.csv`, text)
  }

  const toggleDetail = (key) => setParam({ detail: detail === key ? '' : key })
  const tile = (key) => ({ onClick: () => toggleDetail(key), active: detail === key })

  // ── header controls ──────────────────────────────────────────────────────
  const memberOptions = (members ? members.list : []).map((m) => ({
    value: String(m.email || '').toLowerCase(),
    label: `${m.name || m.email} · ${(ROLE_DEFS[roleOrEmpty(m.role)] || {}).label || m.role || 'member'}`,
  }))
  const ownerOptions = [{ value: '', label: 'No HubSpot identity' }, ...ownerList.map((o) => ({ value: o.id, label: `${o.name || o.email}${o.email ? ` · ${o.email}` : ''}` }))]
  // The presets, plus the retired key a shared link may still carry — so that
  // link keeps its own window, named, instead of snapping to a preset it never
  // meant while the numbers underneath change.
  const rangeOptions = [
    ...RANGE_KEYS.map((k) => ({ value: k, label: RANGE_LABELS[k] })),
    ...(LEGACY_RANGE_KEYS.includes(rangeKey) ? [{ value: rangeKey, label: `${RANGE_LABELS[rangeKey]} (retired)` }] : []),
  ]
  const [dateDraft, setDateDraft] = useState(null) // null = mirror ?from / ?to

  // What the two date fields show: the draft while it is being edited, else the
  // link's own dates, else — whenever rangeFor had to correct them, which is
  // exactly when range.notice is set — the window that was ACTUALLY used. The
  // fields must never disagree with the tiles; the notice says what changed,
  // and because the fields now differ from the URL, Apply lights up so the
  // reader can write the corrected window back into the link they share.
  const linkDatesUsable = !range.notice
  const draftFrom = dateDraft ? dateDraft.from : linkDatesUsable && isDayKey(fromParam) ? fromParam : range.sinceDay || todayDay
  const draftTo = dateDraft ? dateDraft.to : linkDatesUsable && isDayKey(toParam) ? toParam : range.untilDay
  const customDirty = draftFrom !== fromParam || draftTo !== toParam
  const customReady = isDayKey(draftFrom) && isDayKey(draftTo)

  function chooseRange(key) {
    setDateDraft(null)
    if (key !== 'custom') {
      setParam({ range: key, from: null, to: null, day: null })
      return
    }
    // Custom opens on the window already on screen: picking it changes nothing
    // until Apply, and the URL is shareable the moment it is picked.
    //
    // 'All time' is the one preset with no start day, and seeding today there
    // would collapse the visible window from every recorded day to a single
    // one the instant the option is picked — the largest jump of the seven,
    // and a silent one, because a valid pair leaves range.notice empty. So
    // seed from the first day actually on screen instead; only a genuinely
    // empty ledger falls through to today.
    const firstOnScreen = days.length ? days[0].day : todayDay
    setParam({ range: 'custom', from: range.sinceDay || firstOnScreen, to: range.untilDay, day: null })
  }

  function applyCustomRange() {
    if (!customReady) return
    setParam({ range: 'custom', from: draftFrom, to: draftTo, day: null })
    setDateDraft(null)
  }

  // THE CUSTOM WINDOW'S TWO DATE FIELDS AND APPLY. One definition, drawn in two
  // places — the summary toolbar and the calendar page's own date row
  // (2026-09-16) — so the draft, the fences and Enter-to-apply behave the same
  // wherever the window is edited. Two native date fields rather than a
  // calendar widget: the browser's own control is keyboard-accessible,
  // localised and already understood, and the value it hands back
  // ('YYYY-MM-DD') is the Chicago day key the rest of this page is built on.
  // `max` stops a future day being picked at all; a future day that arrives in
  // the URL anyway is pulled back by rangeFor and reported.
  const customDateFields = customMode ? (
    <>
      <input
        className={`${INPUT} !w-[150px]`}
        type="date"
        aria-label="Range start date"
        title="First day of the range (America/Chicago)"
        value={draftFrom}
        // Never past the other end, and never past today: the picker itself
        // cannot build a backwards or future range.
        max={isDayKey(draftTo) && draftTo < todayDay ? draftTo : todayDay}
        onChange={(e) => setDateDraft({ from: e.target.value, to: draftTo })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') applyCustomRange()
        }}
      />
      <input
        className={`${INPUT} !w-[150px]`}
        type="date"
        aria-label="Range end date"
        title="Last day of the range, included in full (America/Chicago)"
        value={draftTo}
        min={isDayKey(draftFrom) ? draftFrom : undefined}
        max={todayDay}
        onChange={(e) => setDateDraft({ from: draftFrom, to: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') applyCustomRange()
        }}
      />
      <button
        type="button"
        className={GHOST_BTN}
        onClick={applyCustomRange}
        disabled={!customReady || !customDirty}
        title={customReady ? 'Read the ledger and the HubSpot mirror over these days' : 'Pick both a start and an end date'}
      >
        Apply
      </button>
    </>
  ) : null

  // The window the STREAK ledger covers, said in the same voice as
  // range.label. Its detail page is captioned with this rather than with the
  // range, because the whole reason it is a separate table is that it does not
  // cover the range.
  const streakWindowLabel = runsWindow.sinceDay ? dayRangeLabel(runsWindow.sinceDay, runsWindow.untilDay) : 'every recorded day'
  // THE RANGE ENDS TODAY, so every HubSpot count on the page is a partial day
  // read from a mirror that syncs on a schedule. That used to be a paragraph
  // above Results in HubSpot; it belongs on the sync line, which is the one place a
  // reader already looks to ask how fresh the mirror is. Three words in the
  // line, the whole caveat in its tooltip.
  const partialToday = resultsOk && range.untilDay === todayDay
  const partialTodayTitle = partialToday
    ? 'The range runs to today, so the HubSpot numbers on this page cover only what had reached the mirror by this sync — a partial day, not a finished one. Operation runs are logged by this app the moment they happen and are not affected.'
    : ''

  // WHAT THE CALL COLUMNS ARE ALLOWED TO SAY. HubSpot returns this portal's
  // call dispositions and durations empty, so the sync drops the keys and both
  // columns are dashes for every row. A column of unexplained dashes reads as a
  // broken page, and the reader's own guess ("no answer", "0 minutes") is worse
  // than the truth — so the tile that opens them says which it is. It says so
  // only when the rows in hand actually prove it, and never about a list the
  // server had to trim to its byte budget: a claim about rows nobody was sent
  // is exactly the confident wrong number this page exists to refuse.
  const callCoverage = useMemo(() => callOutcomeCoverage(metricsData), [metricsData])
  const callsUnrecorded = Boolean(
    callCoverage && callCoverage.rows > 0 && !callCoverage.truncated && callCoverage.withOutcome === 0 && callCoverage.withDuration === 0,
  )
  // How many of the range's calls HubSpot itself placed and recorded
  // (source VOIP). null until a mirror that counts it answers.
  const callsPhone = metricsData && metricsData.totals && metricsData.totals.calls_phone != null ? countOf(metricsData.totals.calls_phone) : null
  // How the range's calls were made, from the mirror's exact per-source
  // counts: the HubSpot phone number, a meeting integration's recording, or
  // typed in by hand. A mirror a deploy behind sends no calls_by_source.
  const callsBySource =
    metricsData && metricsData.totals && metricsData.totals.calls_by_source && typeof metricsData.totals.calls_by_source === 'object'
      ? metricsData.totals.calls_by_source
      : null
  const callsSourceNote = callsBySource
    ? [
        `${num(callsBySource.VOIP || 0)} via HubSpot number`,
        callsBySource.INTEGRATIONS_PLATFORM ? `${num(callsBySource.INTEGRATIONS_PLATFORM)} integration` : '',
        callsBySource.MANUAL ? `${num(callsBySource.MANUAL)} by hand` : '',
        ...Object.entries(callsBySource)
          .filter(([k, v]) => v && !['VOIP', 'INTEGRATIONS_PLATFORM', 'MANUAL'].includes(k))
          .map(([k, v]) => `${num(v)} ${callSourceLabel({ source: k }).toLowerCase()}`),
      ]
        .filter(Boolean)
        .join(' · ')
    : callsPhone != null
      ? `${num(callsPhone)} on the HubSpot number`
      : `${num(s.results.callCompanies)} companies`
  const callsTitle = `Calls made by this member — placed or taken on HubSpot’s phone number (HubSpot records those itself, and the mirror transcribes them), recorded through a HubSpot meeting integration, or logged by hand in HubSpot — attributed by the call’s owner. Deleted and merged calls are excluded.${
    callsUnrecorded
      ? ' HubSpot recorded no outcome and no duration for any of the calls in this range, so both columns show a dash — the portal holds no value, which is not a zero-length call and not an unanswered one.'
      : ''
  }`

  // QUIET WHEN EVERYTHING IS NORMAL. This row used to state the obvious on
  // every load — the range (the Select above already says it), the timezone,
  // that identity came from the sign-in token — and buried the two facts that
  // actually change how the numbers should be read. So it now renders NOTHING
  // in the healthy case and speaks only when something is off:
  //   • the mirror's age is unknown, or STALE. With the detail pages' source
  //     line gone this is the only place data age can surface, and a page that
  //     shows month-old numbers as if they were current is the one failure this
  //     page must never have. An UNREAD mirror says nothing here - its "blank,
  //     not zero" line was taken off on request (2026-10-11); the tiles' own
  //     dashes already show the numbers are missing.
  //   • the ledger could not be read at all (its own banner covers the rest).
  //   • work this browser has not uploaded, or impersonated rows excluded —
  //     both mean the tiles are not the whole story.
  // "today partial" is NOT here: it is normal for a window ending today, and
  // the tiles that need it carry it in their own tooltips.
  // `stale` is syncAge's own threshold (30 minutes against a 5-minute sync
  // schedule), not a second opinion invented here — one definition of stale for
  // the whole app.
  const statusLine = [
    !resultsOk
      ? null
      : !lastSync || !lastSync.known
        ? { text: 'HubSpot mirror sync time unknown' }
        : lastSync.stale
          ? { text: `HubSpot mirror ${lastSync.label} — these numbers are that old`, title: partialTodayTitle }
          : null,
    !ledgerOk ? { text: 'Workspace ledger unread' } : null,
    outbox ? { text: `${outbox.toLocaleString()} event${outbox === 1 ? '' : 's'} from this browser not yet uploaded` } : null,
    impersonatedCount ? { text: `${impersonatedCount.toLocaleString()} impersonated event${impersonatedCount === 1 ? '' : 's'} excluded` } : null,
  ].filter(Boolean)

  // THREE STATES, THREE FACES. A number when it is known; the word Loading
  // while its read is in flight; an em dash only when the answer genuinely is
  // not coming. Smaller and dimmer than a value so a tile mid-read never reads
  // as a result — it is a state, not a measurement.
  const Waiting = () => <span className="text-[15px] font-medium text-faint">Loading…</span>
  // EVERY TILE VALUE CARRIES ITS UNIT (2026-10-13, user request: "everything
  // here needs a unit") — "3 companies", "1 reply", "14 calls". The unit sits
  // after the number in the note's weight so the figure still leads, and it is
  // drawn ONLY beside a real number: a tile that is loading or unavailable says
  // so on its own, and "— calls" would read as a measurement.
  const dash = (ok, v, loading = false, unit = null) => {
    if (!ok) return loading ? <Waiting /> : '—'
    if (!unit || v == null) return num(v)
    return (
      <>
        {num(v)}
        <span className="ml-1.5 text-[15px] font-medium tracking-normal text-dim">{plural(v, unit[0], unit[1])}</span>
      </>
    )
  }
  // A RATE TILE: the same three states, a one-decimal percentage whose unit is
  // the % sign. A rate with an empty denominator (nothing sent, nothing
  // tracked) is a dash — "0%" over no sends would be a measurement nobody took.
  const rateDash = (ok, v, loading = false) => {
    if (!ok) return loading ? <Waiting /> : '—'
    if (v == null) return '—'
    return (
      <>
        {(Math.round(v * 1000) / 10).toLocaleString()}
        <span className="ml-0.5 text-[15px] font-medium tracking-normal text-dim">%</span>
      </>
    )
  }
  const sendsKnown = resultsOk && s.results.emailsSent != null
  const bySource = s.results.emailsBySource
  const apolloSent = bySource && bySource.apollo ? bySource.apollo.emails_sent || 0 : 0

  // ── the detail page's slide ────────────────────────────────────────────────
  //
  // The URL is the source of truth: ?detail=responses is shareable and the
  // browser's own Back works. `overlay` is what to draw and which way it is
  // moving — 'in' while the detail is open, 'out' for the 300ms it takes to
  // leave after the URL has cleared.
  //
  // The motion is a CSS KEYFRAME (styles.css .detail-enter/.detail-exit), not a
  // toggled transform class. A toggle has to paint the element off-screen for
  // one frame first, which means waiting on requestAnimationFrame — and rAF
  // never fires in a background tab, so a page opened in one and switched to
  // later would sit off-screen forever. A keyframe's resting state is
  // "arrived", so the worst a throttled tab can do is skip the animation.
  const [overlay, setOverlay] = useState(() => (detailCfg ? { key: detail, cfg: detailCfg, dir: 'in' } : null))
  const overlayShown = Boolean(detailCfg)
  // The calls detail reads ONE call when ?call= is set: CallView takes the
  // table's place under the same Back button, with its own way back to the rows.
  const isCallView = Boolean(overlay && overlay.key === 'calls' && overlay.cfg.rowDetail === 'call' && callParam)
  // A HubSpot table reads ONE company's whole history when ?thread= names one
  // of its rows: LeadTimelineView takes the table's place the same way. The row
  // is looked up in the rows already read, so a reload waits for them, and a
  // row that is no longer in the range says so instead of opening blank.
  const isThreadView = Boolean(overlay && overlay.cfg.rowDetail === 'thread' && threadParam)
  const threadRow = isThreadView ? detailRows.rows.find((r) => String(r.id) === threadParam) || null : null
  // The calendar page: the density chart and the day timeline under the same
  // Back button, with no table, rows or export of its own.
  // The chart pages: 'calendar' (the day timeline) or 'density' (the
  // activity-density chart), '' for a rows table. They share the header and
  // the date row; each shows its own one card.
  const chartView = overlay && overlay.cfg.view ? overlay.cfg.view : ''
  const isChartView = Boolean(chartView)
  const isCalendarView = chartView === 'calendar'
  const isDensityView = chartView === 'density'
  const exitTimer = useRef(null)
  const hadDetail = useRef(Boolean(detailCfg))

  useEffect(() => {
    if (exitTimer.current) {
      clearTimeout(exitTimer.current)
      exitTimer.current = null
    }
    if (detailCfg) {
      setOverlay((prev) => (prev && prev.key === detail && prev.dir === 'in' ? prev : { key: detail, cfg: detailCfg, dir: 'in' }))
      hadDetail.current = true
      return () => {}
    }
    // Nothing to animate out of on first paint — only when a detail was really
    // open a moment ago.
    if (!hadDetail.current) {
      setOverlay(null)
      return () => {}
    }
    hadDetail.current = false
    setOverlay((prev) => (prev ? { ...prev, dir: 'out' } : null))
    // Matches the 300ms keyframe; unmounting sooner cuts the slide-out short.
    exitTimer.current = setTimeout(() => setOverlay(null), 320)
    return () => {}
  }, [detail, detailCfg])

  useEffect(() => () => clearTimeout(exitTimer.current), [])

  const closeDetail = useCallback(() => setParam({ detail: '', call: '', thread: '', via: '' }), [setParam])

  // Where Back leads. A table opened from a block on the calendar page goes
  // back to the calendar (detail to detail, so it replaces — the browser's own
  // Back still returns to the summary in one step); everything else goes to
  // the summary.
  // The chart page this table was opened from ('calendar' or 'density'), or
  // '' when Back goes to the summary.
  const backToChart = Boolean(detailCfg) && viaParam && detail !== viaParam ? viaParam : ''
  const goBack = useCallback(() => {
    if (backToChart) setParam({ detail: backToChart, call: '', thread: '', via: '' })
    else closeDetail()
  }, [backToChart, setParam, closeDetail])

  // Escape closes the detail page, the same as Back — nothing else on this page
  // traps the key, and a full-page takeover that only closes by mouse is a trap
  // for anyone working from the keyboard.
  useEffect(() => {
    if (!detailCfg) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') goBack()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [detailCfg, goBack])

  return (
    // Capped so charts don't sprawl on wide monitors, but flush left so the
    // header lines up with every other page (never mx-auto).
    //
    // `relative` + `overflow-hidden`: the detail is a PAGE, not a panel under
    // the tiles, and it slides in over this container from the right. Clipping
    // here is what makes the slide read as one surface moving rather than a
    // card appearing — and it keeps the incoming page from widening the layout
    // while it is still off-screen.
    // FULL CONTENT WIDTH, not the reading column. The slide is a whole-page
    // move, so the moving surface has to be the whole page area — capped at
    // 1100px it painted a tall rectangle inside the content pane and the two
    // halves of the screen slid at different widths. The reading cap moves
    // DOWN onto the summary, which is the thing that wants a measure; the
    // detail keeps the full width, which a 15-column ledger needs anyway.
    <LeadLinkContext.Provider value={leadLinkValue}>
    <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden">
      <div
        // The summary steps aside rather than vanishing: a short shift left
        // under the incoming page gives the two views a spatial relationship,
        // so Back feels like walking back rather than re-rendering. `both`
        // fill-mode on the keyframe holds it aside for as long as the detail
        // is open, so this needs no transform class of its own.
        // SCROLLS ITSELF. The container above clips both axes so the detail
        // page can slide in from the right without widening the layout — which
        // also means the app shell's own scroll (AppLayout's overflow-y-auto)
        // can no longer reach this content, and Also tracked sat below the fold
        // with no way down. So the summary owns its vertical scroll; the detail
        // page owns its own (its table fills and scrolls internally). pb-2
        // keeps the last card off the clipped edge.
        // FULL WIDTH, no reading cap — user request, 2026-08-28. The 1100px
        // measure is for prose; this page is four rows of tiles, three charts
        // and a 15-column ledger, and capping it stranded empty space beside
        // the tiles while making the summary NARROWER than the detail page it
        // slides into (the page appeared to change width as you walked in).
        // The charts are no longer here at all: the density chart and the
        // day timeline moved to the calendar page (2026-09-11), which takes
        // this same full width (WIDE_CARD) — so the summary is the tiles.
        className={`flex min-h-0 w-full flex-1 flex-col overflow-y-auto ${
          overlayShown ? 'summary-aside pointer-events-none' : overlay ? 'summary-back' : ''
        }`}
        aria-hidden={overlayShown}
      >
      {/* The frame: the shell's 24px top + this page's old pb-2 under the last card. */}
      <div className={`${PAGE_FRAME} flex min-h-full flex-col pb-8 pt-6 max-md:pb-6 max-md:pt-4`}>
      <PageHeader title="Activity" />

      {/* THE CONTROLS GET THEIR OWN ROW, not the header's right-hand side.
          Six controls (three selects, an address field and two buttons) beside
          a two-line title wrapped into a ragged block that half-covered the
          subtitle and pushed the address field onto its own orphan line. On
          their own full-width row they read as one toolbar: what you are
          looking at (member · range · HubSpot identity), then the actions. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {
          <>
            <Select
              ariaLabel="Member"
              value={member}
              onChange={(v) => setParam({ member: v, owner: null, addresses: null, detail: detail, call: null, thread: null, day: null })}
              options={memberOptions}
              placeholder={members === null ? 'Loading roster…' : 'Member'}
              disabled={members === null || !memberOptions.length}
              buttonClassName={SELECT_BTN}
            />
            <Select
              ariaLabel="Range"
              value={rangeKey}
              onChange={chooseRange}
              options={rangeOptions}
              buttonClassName="min-w-[150px]"
            />
            {/* THE CUSTOM PICKER SITS IN THE SAME ROW, revealed by the Custom Date
                option, and wraps with everything else on a narrow screen. Two
                native date fields rather than a calendar widget: the browser's
                own control is keyboard-accessible, localised and already
                understood, and the value it hands back ('YYYY-MM-DD') is the
                Chicago day key the rest of this page is built on. `max` stops
                a future day being picked at all; a future day that arrives in
                the URL anyway is pulled back by rangeFor and reported. */}
            {customDateFields}
            <Select
              ariaLabel="HubSpot identity"
              value={identity.ownerId}
              onChange={(v) => chooseIdentity(v)}
              options={ownerOptions}
              placeholder={owners === null ? 'Loading HubSpot owners…' : 'HubSpot identity'}
              disabled={owners === null}
              buttonClassName="min-w-[200px]"
            />
            {/* The "extra sending addresses" field was removed on the user's
                instruction, 2026-08-28. It let a viewer name additional
                addresses a member sends from, but the HubSpot identity picker
                beside it already resolves the sending addresses from the
                owner record, so the field was a manual override for a case the
                automatic match handles. `?addresses=` is still READ (see
                identity, above): a link somebody saved or pasted into a deck
                keeps resolving exactly as it did, because silently changing
                the numbers under a claim already made is worse than an
                unreachable control. Nothing writes the param any more. */}
            {/* Export lives on the detail page, next to the rows it exports —
                a detail is never open while this toolbar is on screen. */}
            <button type="button" className={GHOST_BTN} onClick={refresh} title="Re-read the ledger and the HubSpot mirror">
              <RefreshIcon className="h-[15px] w-[15px] text-dim" />
              Refresh
            </button>
            {/* THE CHARTS ARE THEIR OWN PAGES (2026-09-11; split in two
                2026-10-13): the activity density chart and the day timeline,
                which used to close the summary a long scroll under the tiles.
                Each opens the way a tile opens its rows — slides in from the
                right, Back returns here — so the summary stays the tiles.
                Both need a member, like everything below. */}
            <button
              type="button"
              className={GHOST_BTN}
              onClick={() => setParam({ detail: 'density', call: null, thread: null, via: null })}
              disabled={!member}
              title={member ? 'Which hours (or days) were busy' : 'Pick a member first'}
            >
              <ActivityIcon className="h-[15px] w-[15px] text-dim" />
              View density
            </button>
            <button
              type="button"
              className={GHOST_BTN}
              onClick={() => setParam({ detail: 'calendar', call: null, thread: null, via: null })}
              disabled={!member}
              title={member ? 'The day as a calendar' : 'Pick a member first'}
            >
              <CalendarIcon className="h-[15px] w-[15px] text-dim" />
              View calendar
            </button>
            {/* TODAY'S TILES, MAILED AS A PDF (2026-09-16). Sits beside View
                calendar because it is the other way out of the summary: not
                a page, an email. Needs a member, like the calendar. */}
            <button
              type="button"
              className={GHOST_BTN}
              onClick={sendReport}
              disabled={!member || report.status === 'sending'}
              title={
                member
                  ? 'Email today’s activity as a one-page PDF, with yesterday and the recent per-weekday averages beside each number'
                  : 'Pick a member first'
              }
            >
              <MailIcon className="h-[15px] w-[15px] text-dim" />
              {report.status === 'sending' ? 'Sending…' : 'Send daily report'}
            </button>
          </>
        }
      </div>

      {/* rangeFor had to alter what the URL asked for (a reversed pair, a date
          in the future, a half-written link). The numbers below are for the
          window named here, and saying so is the whole point. */}
      {range.notice && <div className={WARN_BOX}>{range.notice}</div>}
      {/* The daily report's outcome, in the report's own words: who got it and
          when, or the lambda's reason it did not go out. Cleared by the next
          send, not by a timer — a confirmation that vanishes is a question. */}
      {report.status === 'sent' && <div className={OK_BOX}>{report.text}</div>}
      {report.status === 'error' && <div className={WARN_BOX}>{report.text}</div>}

      {statusLine.length > 0 && (
      <div className="mb-4 flex flex-wrap gap-x-2 gap-y-1 text-[12.5px] text-dim">
        {statusLine.map((part, i) => (
          <span key={part.text} className="whitespace-nowrap" title={part.title || undefined}>
            {i > 0 && <span className="mr-2 text-faint">·</span>}
            {part.text}
          </span>
        ))}
      </div>
      )}

      {/* Shown ONLY when the identity did not resolve. A matched owner needs no
          announcement — but an UNMATCHED one silently zeroes every HubSpot tile,
          and without a line saying so the page looks like a rep who did nothing. */}
      {member && owners !== null && !owner && (
        <div className="mb-5 text-[12.5px] text-dim">
          HubSpot identity:{' '}
          {owner ? (
            <>
              <span className="font-semibold text-ink">{owner.name || owner.email}</span>
              {owner.email ? ` (${owner.email})` : ''} — matched by {identity.how}
            </>
          ) : identity.ownerId ? (
            // The id is still sent to the metrics request (the server may
            // attribute blank-from sends by owner id), so the page must not
            // claim address-only attribution just because the id cannot be
            // shown with a name.
            <span>
              using the {identity.how} HubSpot owner id <span className="font-mono">{identity.ownerId}</span> —{' '}
              {ownerList.length ? 'not in the current owners list' : 'the owners list could not be read'}; sends may still be
              attributed to this member by that id
            </span>
          ) : (
            <span>
              none matched {ownerList.length ? '— results count only sends from the member’s own address' : '— the mirror listed no owners'}
            </span>
          )}
          {extraAddresses.length > 0 && (
            <>
              {' '}
              · also counting sends from <span className="font-mono">{extraAddresses.join(', ')}</span>
            </>
          )}
          {metricsData && Array.isArray(metricsData.senders) && metricsData.senders.some((x) => !x.attributed) && (
            <>
              {' '}
              · unattributed sender addresses seen in the mirror:{' '}
              {metricsData.senders
                .filter((x) => !x.attributed)
                .slice(0, 4)
                .map((x) => `${x.address} (${Number(x.count || 0).toLocaleString()})`)
                .join(', ')}
            </>
          )}
        </div>
      )}

      {ledgerLoading ? (
        <PanelLoading label={`Loading ${memberName || 'the member'}’s record…`} />
      ) : !member ? (
        <div className={BOX}>
          {members && members.list.length === 0
            ? 'The roster is empty, so there is nobody to report on. Invite a member in Collaboration.'
            : 'Pick a member to see their record.'}
        </div>
      ) : (
        <>
          {summary.status === 'error' && (
            <div className="mb-5 rounded-[12px] border border-[#fde68a] bg-[#fefce8] p-4 text-[13px] text-[#a16207] dark:border-[rgba(161,98,7,0.4)] dark:bg-[rgba(161,98,7,0.14)] dark:text-[#fbbf24]">
              {ledgerFailure({ status: summary.httpStatus })} The routine, Working the drafts and Also tracked show dashes until the ledger can be read.
            </div>
          )}
          {ledgerOk && summaryData && summaryData.complete === false && (
            <div className="mb-5 rounded-[12px] border border-[#fde68a] bg-[#fefce8] p-4 text-[13px] text-[#a16207] dark:border-[rgba(161,98,7,0.4)] dark:bg-[rgba(161,98,7,0.14)] dark:text-[#fbbf24]">
              The ledger summary stopped early (time budget) — event counts for this range are incomplete and may be short.
            </div>
          )}
          {incompleteEager.length > 0 && (
            <div className="mb-5 rounded-[12px] border border-[#fde68a] bg-[#fefce8] p-4 text-[13px] text-[#a16207] dark:border-[rgba(161,98,7,0.4)] dark:bg-[rgba(161,98,7,0.14)] dark:text-[#fbbf24]">
              The {incompleteEager.map((t) => EAGER_LABELS[t]).join(', ')} read
              {incompleteEager.length === 1 ? '' : 's'} stopped early (time budget) — the tiles computed from those rows may be
              short.
            </div>
          )}

          <h2 className={SECTION_H}>Working the Drafts</h2>
          <section className={`mb-6 ${KPI_ROW}`}>
            <KpiShell label="Companies Copied" icon={BuildingIcon} note="unique lead keys" {...tile('copies')}>
              {dash(draftsOk, s.drafts.companiesCopied, draftsLoading, ['company', 'companies'])}
            </KpiShell>
            <KpiShell label="Emails Copied" icon={CopyIcon} note="Copy email presses" {...tile('copies')}>
              {dash(draftsOk, s.drafts.emailsCopied, draftsLoading, ['email', 'emails'])}
            </KpiShell>
            <KpiShell label="Subjects Copied" icon={CopyIcon} note="Copy subject presses" {...tile('copies')}>
              {dash(draftsOk, s.drafts.subjectsCopied, draftsLoading, ['subject', 'subjects'])}
            </KpiShell>
            <KpiShell label="HubSpot Opened" icon={MailIcon} note={draftsOk ? `${num(s.drafts.hubspotOpenCompanies)} unique companies` : 'unique companies'} {...tile('hubspot_opens')}>
              {dash(draftsOk, s.drafts.hubspotOpens, draftsLoading, ['open', 'opens'])}
            </KpiShell>
          </section>

          <h2 className={SECTION_H}>Results in HubSpot</h2>
          {/* The partial-day paragraph that used to sit here is gone. The fact
              it carried — a range ending today is a partial day off a mirror
              that syncs on a schedule — is on the status line at the top
              ("HubSpot mirror synced Xm ago · today partial", with the whole
              sentence in its tooltip), which is where a reader already looks to
              ask how fresh these numbers are. */}
          {metrics.status === 'error' && (
            <div className="mb-3 rounded-[12px] border border-[#fde68a] bg-[#fefce8] p-4 text-[13px] text-[#a16207] dark:border-[rgba(161,98,7,0.4)] dark:bg-[rgba(161,98,7,0.14)] dark:text-[#fbbf24]">
              {metrics.reason || metricsFailure(metrics.data)} Results are not shown.
            </div>
          )}
          <section className={`mb-6 ${KPI_ROW}`}>
            <KpiShell label="Companies Reached" icon={BuildingIcon} note={resultsOk ? `${num(s.results.firstTouches)} for the first time` : metrics.status === 'loading' ? 'reading the mirror…' : 'unavailable'} {...tile('outreach')}>
              {dash(resultsOk, s.results.companiesReached, resultsLoading, ['company', 'companies'])}
            </KpiShell>
            <KpiShell label="Outreach Emails" icon={MailIcon} note="to companies that haven't replied" {...tile('outreach')}>
              {dash(resultsOk, s.results.outreachEmails, resultsLoading, ['email', 'emails'])}
            </KpiShell>
            <KpiShell label="Follow-Ups" icon={MailIcon} note={resultsOk ? `${num(s.results.repliesSent)} ${plural(s.results.repliesSent, 'reply', 'replies')} sent` : 'replies sent —'} {...tile('followups')}>
              {dash(resultsOk, s.results.followups, resultsLoading, ['email', 'emails'])}
            </KpiShell>
            <KpiShell
              label="Responses"
              icon={ChatIcon}
              // The percentage is all-time on both sides (see summarize): a
              // reply in this range usually answers an email sent before it,
              // so "of the N companies ever reached" is the only share that
              // compares like with like. The note says so out loud.
              note={
                resultsOk
                  ? `${num(s.results.respondingCompanies)} ${plural(s.results.respondingCompanies, 'company', 'companies')} replied${
                      s.results.responseRate === null
                        ? ''
                        : ` · ${pct(s.results.responseRate)} of the ${num(s.results.companiesReachedAllTime)} ever reached`
                    }`
                  : 'responding companies · rate'
              }
              labelTitle="Human replies to this rep's emails inside the range (auto-replies and robots excluded). The percentage is all-time: companies that ever replied, over companies ever reached."
              {...tile('responses')}
            >
              {dash(resultsOk, s.results.responses, resultsLoading, ['reply', 'replies'])}
            </KpiShell>
          </section>

          {/* DELIVERY AND OPENS (2026-10-20). Every send the rep made, bounces
              included, read off HubSpot's own tracker: Bo's Gmail-connected
              sends today, and Apollo's sequence emails once Apollo logs them
              into HubSpot (the Sent via column and the first tile's note
              split the two). All four open the same rows. */}
          <h2 className={SECTION_H}>Delivery &amp; Opens</h2>
          <section className={`mb-6 ${KPI_ROW}`}>
            <KpiShell
              label="Emails Sent"
              icon={SendIcon}
              note={
                sendsKnown
                  ? apolloSent
                    ? `${num(apolloSent)} via Apollo · ${num(s.results.emailsSent - apolloSent)} via HubSpot`
                    : 'bounces included · all via HubSpot'
                  : resultsLoading
                    ? 'reading the mirror…'
                    : 'not counted by this mirror yet'
              }
              labelTitle="Every email this member sent in the range — outreach, follow-ups and replies, plus the ones that bounced or failed. Drafts and scheduled emails were never sent and are left out."
              {...tile('sends')}
            >
              {dash(sendsKnown, s.results.emailsSent, resultsLoading, ['email', 'emails'])}
            </KpiShell>
            <KpiShell
              label="Delivery Rate"
              icon={CheckIcon}
              note={sendsKnown ? `${num(s.results.emailsDelivered)} of ${num(s.results.emailsSent)} delivered · ${num(s.results.emailsBounced)} bounced` : 'delivered · bounced'}
              labelTitle="Delivered sends over all sends. A send is delivered unless HubSpot marked it bounced (the receiving server refused it) or failed."
              {...tile('sends')}
            >
              {rateDash(sendsKnown, s.results.deliveryRate, resultsLoading)}
            </KpiShell>
            <KpiShell
              label="Open Rate"
              icon={ActivityIcon}
              note={sendsKnown ? `${num(s.results.emailsOpened)} of ${num(s.results.emailsTracked)} tracked opened` : 'opened · tracked'}
              labelTitle="Of the delivered sends HubSpot was tracking, the share opened at least once. Apple Mail privacy and corporate scanners pre-load the tracking pixel, so opens run high; image-blocking clients never report one. Counted on the day the email was sent."
              {...tile('sends')}
            >
              {rateDash(sendsKnown, s.results.openRate, resultsLoading)}
            </KpiShell>
            <KpiShell
              label="Click Rate"
              icon={HashIcon}
              note={sendsKnown ? `${num(s.results.emailsClicked)} ${plural(s.results.emailsClicked, 'email', 'emails')} clicked` : 'clicked'}
              labelTitle="Of the delivered sends HubSpot was tracking, the share with at least one link clicked."
              {...tile('sends')}
            >
              {rateDash(sendsKnown, s.results.clickRate, resultsLoading)}
            </KpiShell>
          </section>

          {/* THE HIGHEST-VALUE ACTIVITY ON THE PAGE, and for a long time the
              only work the mirror carried that this page never showed. Calls
              and meetings are how a deal actually moves; a record that charts
              emails and omits hundreds of logged calls understates the rep in
              the one direction nobody in the room ever challenges. Same three
              states as every other mirror tile: a number, "Loading…" while the
              read is in flight, a dash when it is not coming. */}
          <h2 className={SECTION_H}>Calls, Meetings &amp; Notes</h2>
          <section className={`mb-6 ${KPI_ROW}`}>
            <KpiShell
              label="Calls Made"
              icon={PhoneIcon}
              // The split the reader actually asks about now that the rep
              // dials from HubSpot's own number: how many calls HubSpot placed
              // and recorded, and how many were still typed in by hand. A
              // mirror one deploy behind sends no calls_phone, and then the
              // note falls back to the company count rather than a guess.
              note={resultsOk ? callsSourceNote : 'HubSpot number · integration · by hand'}
              labelTitle={callsTitle}
              {...tile('calls')}
            >
              {dash(resultsOk, s.results.calls, resultsLoading, ['call', 'calls'])}
            </KpiShell>
            <KpiShell
              label="Meetings"
              icon={CalendarIcon}
              note={resultsOk ? `${num(s.results.meetingCompanies)} ${plural(s.results.meetingCompanies, 'company', 'companies')}` : 'companies'}
              labelTitle="HubSpot meetings attributed to this member: by the HubSpot user who created the meeting when the record names one, else by the meeting’s owner. Counted on the day the meeting starts when HubSpot recorded a start time, else on the day of the stamp HubSpot dates the record by."
              {...tile('meetings')}
            >
              {dash(resultsOk, s.results.meetings, resultsLoading, ['meeting', 'meetings'])}
            </KpiShell>
            <KpiShell
              label="Notes Written"
              icon={TextIcon}
              note={resultsOk ? `${num(s.results.noteCompanies)} ${plural(s.results.noteCompanies, 'company', 'companies')}` : 'companies'}
              labelTitle="HubSpot notes attributed to this member: by the HubSpot user who created the note when the record names one, else by the note’s owner. A note attached to no company still counts; it just has no company to count."
              {...tile('notes')}
            >
              {dash(resultsOk, s.results.notes, resultsLoading, ['note', 'notes'])}
            </KpiShell>
          </section>

          <h2 className={SECTION_H}>Also Tracked</h2>
          <section className={`mb-6 ${KPI_ROW}`}>
            <KpiShell label="Deals Added" icon={TagIcon} note={resultsOk ? `${num(s.tracked.dealsBy.owner)} by owner · ${num(s.tracked.dealsBy.creator)} by creator` : 'attributed by owner / creator'} {...tile('deals')}>
              {dash(resultsOk, s.tracked.deals, resultsLoading, ['deal', 'deals'])}
            </KpiShell>
            <KpiShell label="HubSpot Tasks Created" icon={CheckSquareIcon} note={resultsOk ? `${num(s.tracked.tasksBy.owner)} by owner · ${num(s.tracked.tasksBy.creator)} by creator` : 'attributed by owner / creator'} {...tile('tasks')}>
              {dash(resultsOk, s.tracked.tasks, resultsLoading, ['task', 'tasks'])}
            </KpiShell>
            <KpiShell
              label="Operation Runs"
              icon={CheckSquareIcon}
              note={
                routineOk
                  ? `${num(s.tracked.runs.built)} built · ${num(s.tracked.runs.nothing)} nothing due · ${num(s.tracked.runs.failed + s.tracked.runs.aborted)} failed${s.tracked.runs.unreadable ? ` · ${num(s.tracked.runs.unreadable)} unreadable` : ''}`
                  : 'built / nothing / failed'
              }
              {...tile('runs')}
            >
              {dash(routineOk, s.tracked.runs.total, routineLoading, ['run', 'runs'])}
            </KpiShell>
            <KpiShell
              label="Sign-Ins"
              icon={UserIcon}
              note={
                ledgerOk
                  ? `${num(s.tracked.activeDays)} active days${s.tracked.firstAt ? ` · ${chicagoDayOf(s.tracked.firstAt)} – ${chicagoDayOf(s.tracked.lastAt)}` : ''}`
                  : 'active days · first – last activity'
              }
              {...tile('signins')}
            >
              {dash(ledgerOk, s.tracked.signIns, ledgerLoading, ['sign-in', 'sign-ins'])}
            </KpiShell>
          </section>
        </>
      )}
      </div>
      </div>

      {/* THE DETAIL IS ITS OWN PAGE. It slides in from the right edge of this
          container and back out again, so the tiles and their evidence read as
          two places rather than one long scroll — the number you pressed stays
          where you left it, and Back returns to it. Kept mounted through the
          exit so the slide-out can finish; keyed on the detail id so each table
          mounts fresh with its own columns, prefs and sort. */}
      {overlay ? (
        <section
          // bg-panel, NOT bg-bg: the content pane this page sits in is
          // `bg-panel` (Shell.jsx's card), so painting the sliding surface with
          // the app's window colour drew a visibly different rectangle over it.
          // The moving page has to be the same colour as the page it replaces,
          // or the slide reads as a box flying in rather than the view changing.
          className={`absolute inset-0 z-20 flex min-h-0 flex-col bg-panel ${
            overlay.dir === 'in' ? 'detail-enter' : 'detail-exit'
          }`}
          aria-label={
            isCalendarView
              ? 'Calendar — the day as a timeline'
              : isDensityView
                ? 'Activity density — which hours were busy'
                : `${overlay.cfg.title} — the rows behind the number`
          }
        >
          <div className={`${PAGE_FRAME} flex min-h-0 flex-1 flex-col py-6 max-md:py-4`}>
          <button
            type="button"
            className="mb-3 inline-flex w-fit cursor-pointer items-center gap-x-[7px] border-none bg-transparent p-0 text-[13px] font-semibold text-dim transition-colors hover:text-ink focus-visible:[outline:2px_solid_var(--primary)] focus-visible:[outline-offset:3px]"
            onClick={goBack}
          >
            {/* The arrow leads the label back the way the page came in. */}
            <span aria-hidden="true" className="text-[15px] leading-none">←</span>
            {backToChart ? `Back to ${DETAILS[backToChart].title.toLowerCase()}` : 'Back to Activity'}
          </button>

          {isCallView ? (
            <CallView id={callParam} memberName={memberName} onBack={() => setParam({ call: '' })} />
          ) : isThreadView ? (
            threadRow ? (
              <LeadTimelineView
                key={threadParam}
                lead={threadLeadOf(threadRow)}
                companyId={String(threadRow.company_id || '')}
                hubspotUrl={threadRow.url_kind === 'company' ? threadRow.hubspot_url || '' : ''}
                onBack={() => setParam({ thread: '' })}
                backLabel={`Back to ${overlay.cfg.title}`}
                intro={false}
              />
            ) : detailRows.status === 'loading' ? (
              <PanelLoading label="Reading rows…" />
            ) : (
              <div className={LOADING}>
                <span>
                  That row is not in this range.{' '}
                  <button
                    type="button"
                    className="cursor-pointer border-none bg-transparent p-0 font-semibold text-primary-dark hover:underline"
                    onClick={() => setParam({ thread: '' })}
                  >
                    Back to {overlay.cfg.title}
                  </button>
                </span>
              </div>
            )
          ) : isChartView ? (
            <>
              {/* THE CHART PAGES (calendar, density). The same header row as a
                  table page — the title, who and when, Refresh — without a row
                  count or an Export, because there is nothing here to count or
                  export: the one chart below is the whole page. */}
              <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                <h1 className="m-0 min-w-0 flex-shrink truncate text-[24px] font-bold tracking-[-0.02em] text-ink">{overlay.cfg.title}</h1>
                <span className="text-[12.5px] text-dim">
                  {memberName} · {range.label}
                </span>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  <button type="button" className={GHOST_BTN} onClick={refresh} title="Re-read the ledger and the HubSpot mirror">
                    <RefreshIcon className="h-[15px] w-[15px] text-dim" />
                    Refresh
                  </button>
                </div>
              </div>
              {/* THE DATE CONTROLS, ON THE PAGE THAT IS ABOUT DATES (user request,
                  2026-09-16: "needs to be able to filter dates while inside the
                  calendar view"). Until now the range lived only on the summary
                  toolbar, which this page slides over, so the calendar opened on
                  Today with no way to look at any other day. The presets are
                  chips rather than the toolbar's Select: every window is visible
                  at once and the current one is lit, which on a page whose whole
                  subject is "which days" is worth the row it takes. The same
                  chooseRange and custom fields as the toolbar, so the URL and the
                  numbers behave identically wherever the range is changed. */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <div role="group" aria-label="Calendar range" className="flex flex-wrap items-center gap-2">
                  {rangeOptions.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={rangeKey === o.value}
                      className={rangeKey === o.value ? RANGE_CHIP_ON : RANGE_CHIP_OFF}
                      onClick={() => {
                        if (rangeKey !== o.value) chooseRange(o.value)
                      }}
                      title={o.value === 'custom' ? 'Pick a first and a last day' : undefined}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {customDateFields}
              </div>
              {/* rangeFor had to alter what the URL asked for — said here too, on
                  the page where the dates are now edited. */}
              {range.notice && <div className={WARN_BOX}>{range.notice}</div>}
              {/* SCROLLS ITSELF, as the summary does: the page container clips
                  both axes for the slide, so this column owns its own vertical
                  scroll, and pb-2 keeps the last card off the clipped edge. The
                  same gates as the summary — no record until the ledger has
                  been read, and no member means there is no day to lay out. */}
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-2">
                {ledgerLoading ? (
                  <PanelLoading label={`Loading ${memberName || 'the member'}’s record…`} />
                ) : !member ? (
                  <div className={BOX}>Pick a member to see their day.</div>
                ) : (
                  <>
                    {/* Charts. The caption paragraphs these three used to carry are gone;
                        what each bar IS now lives in the heading and its tooltip, and the
                        reason a chart is blank lives where the chart would have been —
                        never as an empty chart, which reads as a quiet day. The one
                        exception is a range the mirror READ and found empty: that is a
                        fact we have, so it gets bars of real zeros, drawn like any other
                        range, rather than a sentence where the chart should be. */}

                    {/* WHICH HOURS THE REP IS ACTUALLY WORKING — first, because it is the
                        only chart on the page that answers a question about people rather
                        than about the app. THE READER PICKS THE BUCKET here (the toggle in
                        this card's header, held in ?density=): the mirror used to choose
                        it from the span of the range alone, and a browser can sum hours
                        into days but can never split days or weeks back into hours, so
                        asking the server is the only way anyone can see the hour-of-day
                        profile of a window longer than a day. THE FULL WIDTH OF THE PAGE
                        is this card's — see WIDE_CARD. ITS OWN PAGE since 2026-10-13
                        (?detail=density); the timeline is on ?detail=calendar. */}
                    {isDensityView && (
                    <section className={WIDE_CARD}>
                      {/* Heading left, toggle hard right, one row. They wrap onto two
                          lines on a narrow pane rather than the toggle shrinking: a
                          two-segment control that has to be aimed at is worse than a
                          second line. */}
                      <div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                        <h3
                          className="m-0 text-[14px] font-semibold text-ink"
                          // THE ONE CAVEAT THAT PROTECTS A PERSON, and nothing this page
                          // cannot prove. The chart gets read as "which hours is the rep
                          // working", about someone by name, so the sentence has to say
                          // that what it measures is when activity was LOGGED IN HUBSPOT —
                          // the same thing for a rep working online in Central time, not
                          // the same thing for one who works offline and syncs later or
                          // sits in another zone. Everything else in it is built from the
                          // payload (densityTitle): the kinds are the mirror's own labels
                          // and the clock is named only when the mirror named it.
                          title={densityTitle}
                        >
                          Activity density
                        </h3>
                        {/* THE HOURLY / DAILY TOGGLE — the app's own segmented control
                            (DraftFlowView's Main | Developer toggle), same chrome, same
                            two real buttons with aria-pressed. Buttons and not a Select
                            because there are exactly two of them, both always reachable
                            by keyboard, and which one is on is a fact the reader should
                            be able to see without opening anything.

                            The title on each says what the segment MEANS, which for
                            'Hourly' over a multi-day range is not what its name suggests
                            — see densityViewTitle. NEITHER IS EVER DISABLED: a view this
                            mirror cannot answer explains itself in a sentence below,
                            which a reader can act on, where a greyed-out button says
                            nothing at all.

                            Pressing one REPLACES the history entry. It adjusts the view
                            in place, exactly as the member and the range do, and it is
                            not a page; pushing would bury the way out of this page under
                            a row of near-identical entries — see the setParam comment. */}
                        <div
                          role="group"
                          aria-label="Activity density view"
                          className="inline-flex flex-none items-center gap-x-[2px] rounded-[10px] border border-solid border-line bg-surface p-[3px]"
                        >
                          {DENSITY_VIEWS.map((v) => (
                            <button
                              key={v}
                              type="button"
                              aria-pressed={densityView === v}
                              title={densityViewTitle(v)}
                              onClick={() => setParam({ density: v })}
                              className={
                                densityView === v
                                  ? 'cursor-pointer rounded-[7px] bg-primary px-3 py-[5px] text-[13px] font-semibold text-white'
                                  : 'cursor-pointer rounded-[7px] bg-transparent px-3 py-[5px] text-[13px] font-medium text-dim transition-colors hover:text-ink'
                              }
                            >
                              {DENSITY_VIEW_LABELS[v]}
                            </button>
                          ))}
                        </div>
                      </div>
                      {densityNote ? (
                        <div className="py-6 text-center text-[13px] text-faint">{densityNote}</div>
                      ) : densityMismatch ? (
                        // THE MIRROR ANSWERED A DIFFERENT QUESTION. Bars under the wrong
                        // label would be the quietest lie on this page, so the sentence
                        // takes the chart's place exactly as an unreadable mirror does.
                        <div className="py-6 text-center text-[13px] text-faint">{densityMismatch}</div>
                      ) : densityChart ? (
                        <>
                          {/* NO SUBTITLE, AND NO SENTENCE VOUCHING FOR A FLAT CHART — both
                              removed at the user's request. What a bar is lives in the
                              toggle's and the bars' own tooltips, the hours that have not
                              happened carry the faint band, and a drawn chart already
                              means the mirror was read (an unread one gets densityNote's
                              sentence in its place). What is left is only ever a fact the
                              bars cannot show, printed only when it is true. */}
                          {densityCaption ? <div className="mb-3 text-[12px] text-faint">{densityCaption}</div> : null}
                          <BarChart
                            data={densityData}
                            keyOf={(d) => d.key}
                            labelEvery={densityPlan.every}
                            // Phased so the busiest bucket keeps its label — see
                            // densityLabelPlan.
                            labelOffset={densityPlan.offset}
                            height={200}
                            formatValue={(v) => `${v.toLocaleString()} ${v === 1 ? 'activity' : 'activities'}`}
                          />
                        </>
                      ) : (
                        // A GENUINE ZERO WITH NO AXIS TO DRAW IT ON. densityAxis needs a
                        // first day and a last day to count bars between, and an all-time
                        // range has no first day — so this is the one empty range that
                        // still gets a sentence where every other one now gets a chart of
                        // real zeros.
                        //
                        // The undatable sentence is the library's (densityZeroCaption),
                        // because it names a count this page would otherwise have to
                        // restate. A range that simply holds nothing has no library
                        // sentence any more — under a chart the flat bars say it — so
                        // here, with no bars to say it, the page says it in its own words.
                        <div className="py-6 text-center text-[13px] text-faint">
                          {density && density.undatable > 0
                            ? densityZeroCaption(density)
                            : 'No HubSpot activity in this range — the mirror was read and it holds none.'}{' '}
                          This range has no first day to count bars from, so there is no axis to draw those zeros on.
                        </div>
                      )}
                    </section>
                    )}

                    {/* THE DAY, AS A CALENDAR — its own page (?detail=calendar). Full
                        width, because a column of hours with side-by-side lanes needs
                        the room. The day stepper lives in the card header and is always
                        on: it walks a wider range, and moves a one-day range whole
                        (dayStepPlan) — so Today, the page's default, can step back. */}
                    {isCalendarView && (
                    <section className={WIDE_CARD}>
                      <div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                        <div className="min-w-0">
                          <h3 className="m-0 text-[14px] font-semibold text-ink" title={timelineTitle}>
                            Day timeline
                          </h3>
                          <div className="text-[12px] text-faint">{timelineSummary.join(' · ')}</div>
                        </div>
                        <div className="flex flex-none items-center gap-1">
                          {/* ALWAYS ON (2026-09-16). On a range wider than a day the
                              arrows walk the range and stop at its edges; on a one-day
                              range — Today, the page's default — they move the whole
                              window: the day before, and forward again up to today. So
                              the calendar steps through days without a trip back to the
                              summary. The date field does the same with a picked day. */}
                          <button
                            type="button"
                            className={STEP_BTN}
                            onClick={() => stepPlan.back && setParam(stepPlan.back)}
                            disabled={!stepPlan.back}
                            title={stepPlan.single ? 'The day before — the range moves with it' : 'The day before'}
                            aria-label="The day before"
                          >
                            <ChevronLeftIcon className="h-[15px] w-[15px]" />
                          </button>
                          <input
                            className={`${INPUT} !w-[150px]`}
                            type="date"
                            aria-label="Timeline day"
                            title={
                              stepPlan.single
                                ? 'Which day to lay out — the range moves to that day (America/Chicago)'
                                : 'Which day of the range to lay out (America/Chicago)'
                            }
                            value={timelineDay}
                            min={stepPlan.single ? undefined : range.sinceDay || undefined}
                            max={stepPlan.single ? todayDay : range.untilDay}
                            onChange={(e) => {
                              const patch = stepPlan.pick(e.target.value)
                              if (patch) setParam(patch)
                            }}
                          />
                          <button
                            type="button"
                            className={STEP_BTN}
                            onClick={() => stepPlan.forward && setParam(stepPlan.forward)}
                            disabled={!stepPlan.forward}
                            title={stepPlan.single ? 'The day after — the range moves with it' : 'The day after'}
                            aria-label="The day after"
                          >
                            <ChevronRightIcon className="h-[15px] w-[15px]" />
                          </button>
                        </div>
                      </div>
                      {/* What is NOT on the column, said before the column: a missing
                          read must never draw as a quiet day. */}
                      {timeline.notes.map((n) => (
                        <div key={n} className="mb-2 text-[12px] text-faint">
                          {n}
                        </div>
                      ))}
                      <DayTimeline timeline={timeline} onOpen={openTimelineBlock} />
                    </section>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
          <>
          <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="m-0 min-w-0 flex-shrink truncate text-[24px] font-bold tracking-[-0.02em] text-ink">{overlay.cfg.title}</h1>
            <span className="rounded-full bg-panel px-[9px] py-[3px] text-[12px] font-semibold text-dim">
              {detailRows.status === 'ok'
                ? `${detailRows.rows.length.toLocaleString()} ${detailRows.rows.length === 1 ? 'row' : 'rows'}`
                : detailRows.status === 'loading'
                  ? 'loading…'
                  : 'unavailable'}
            </span>
            <span className="text-[12.5px] text-dim">
              {memberName} · {overlay.key === 'streak' ? streakWindowLabel : range.label}
            </span>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <button type="button" className={GHOST_BTN} onClick={refresh} title="Re-read the ledger and the HubSpot mirror">
                <RefreshIcon className="h-[15px] w-[15px] text-dim" />
                Refresh
              </button>
              <button
                type="button"
                className={GHOST_BTN}
                onClick={exportDetail}
                disabled={detailRows.status !== 'ok' || !detailRows.rows.length}
                title={`Export the full ${overlay.cfg.title} list as CSV`}
              >
                <DownloadIcon className="h-[15px] w-[15px] text-dim" />
                Export{detailRows.status === 'ok' ? ` (${detailRows.rows.length.toLocaleString()})` : ''}
              </button>
            </div>
          </div>

          <div className="mb-3" />
          {detailRows.warnings.map((w) => (
            <div key={w} className="mb-3 rounded-[10px] border border-[#fde68a] bg-[#fefce8] px-3 py-2 text-[12.5px] text-[#a16207] dark:border-[rgba(161,98,7,0.4)] dark:bg-[rgba(161,98,7,0.14)] dark:text-[#fbbf24]">
              {w}
            </div>
          ))}
          {detailRows.status === 'error' ? (
            <div className={BOX}>{detailRows.warnings[0] || 'Unavailable.'}</div>
          ) : (
            // The table owns the rest of the page and scrolls inside itself on
            // a narrow screen; the page never scrolls sideways.
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-auto">
              <DataTable
                key={overlay.key}
                tableId={`activity.${overlay.key}`}
                columns={overlay.cfg.columns}
                rows={detailRows.rows}
                rowKey={overlay.cfg.rowKey || ((r) => r.id)}
                // The calls ledger opens the call itself on press (CallView); the
                // HubSpot company tables open the company's whole thread
                // (LeadTimelineView).
                onRowClick={
                  overlay.cfg.rowDetail === 'call'
                    ? (r) => setParam({ call: String((r && r.id) || '') })
                    : overlay.cfg.rowDetail === 'thread'
                      ? (r) => setParam({ thread: String((r && r.id) || '') })
                      : undefined
                }
                defaultSort={overlay.cfg.defaultSort || { key: overlay.cfg.columns[0].key, dir: 'desc' }}
                searchable
                searchPlaceholder={`Search ${overlay.cfg.title.toLowerCase()}…`}
                searchKeys={overlay.cfg.searchKeys}
                searchValue={overlay.cfg.searchValue}
                // Export sits in the page header beside Refresh, not in the
                // table toolbar — one button, where the page's other actions are.
                pageSize={50}
                fill
                loading={detailRows.status === 'loading'}
                loadingMessage="Reading rows…"
                emptyMessage={`No ${overlay.cfg.title.toLowerCase()} for ${memberName} in ${overlay.key === 'streak' ? 'the streak window' : 'this range'}.`}
              />
            </div>
          )}
          </>
          )}
          </div>
        </section>
      ) : null}
    </div>
    </LeadLinkContext.Provider>
  )
}
